import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EkycAdmin } from "@/components/EkycAdmin";
import { DeliveryAgencyPicker, DeliveryAgenciesButton } from "@/components/DeliveryAgencyPicker";
import { BadgeCheck } from "lucide-react";
import { openChatSocket } from "@/lib/ws";
import { useNameSort } from "@/lib/useNameSort";
import { ResendBulkPasteModal } from "@/components/ResendBulkPaste";
import BankAccountManager from "@/components/BankAccountManager";
import BroadcastNoticeManager from "@/components/BroadcastNoticeManager";
import TelegramAlertsManager from "@/components/TelegramAlertsManager";
import WalletPage from "@/pages/Wallet";
import {
  LogOut,
  Users,
  MessageCircle,
  Activity,
  Plus,
  Trash2,
  Key,
  Star,
  MessageSquare,
  Radio,
  ArrowLeft,
  Loader2,
  Database,
  Download,
  PlayCircle,
  Flame,
  Sheet,
  Link as LinkIcon,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Copy as CopyIcon,
  KeyRound,
  Hash,
  Pencil,
  Inbox,
  FileSpreadsheet,
  ArrowUpDown,
  Home,
  Wallet,
  Upload,
  Landmark,
  Megaphone,
  Bot,
  Search,
  X as XIcon,
} from "lucide-react";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function Admin() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Support ?tab=<key> deep-linking (e.g. /admin?tab=wallet).
  // Falls back to 'overview' if not provided or unknown.
  const _validTabs = new Set([
    "overview", "users", "groups", "sheets", "realtime",
    "bank", "broadcast", "telegram", "wallet", "backups", "ekyc",
  ]);
  const _initialTab = (() => {
    try {
      const q = new URLSearchParams(location.search).get("tab");
      return q && _validTabs.has(q) ? q : "overview";
    } catch { return "overview"; }
  })();
  const [tab, setTab] = useState(_initialTab);
  // Reflect tab -> URL so it's shareable / survives refresh.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const current = q.get("tab") || "overview";
    if (current !== tab) {
      if (tab === "overview") q.delete("tab");
      else q.set("tab", tab);
      navigate({ pathname: "/admin", search: q.toString() ? `?${q.toString()}` : "" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null); // user obj
  const [complaintCount, setComplaintCount] = useState(0);
  const wsRef = useRef(null);

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const loadComplaintCount = async () => {
    try {
      const { data } = await api.get("/complaints");
      setComplaintCount(data.total_open || 0);
    } catch {
      // best-effort badge
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadOverview(), loadUsers(), loadComplaintCount()]);
      setLoading(false);
    })();
  }, []);

  // Live WebSocket for real-time updates
  useEffect(() => {
    if (!token) return;
    const ws = openChatSocket(token, (data) => {
      if (data.type === "message.new") {
        setOverview((prev) => {
          if (!prev) return prev;
          const streams = prev.group_streams.map((gs) => {
            if (gs.id !== data.group_id) return gs;
            const msgs = [...gs.messages, data.message].slice(-20);
            return { ...gs, messages: msgs };
          });
          return {
            ...prev,
            stats: {
              ...prev.stats,
              total_messages: prev.stats.total_messages + 1,
              messages_today: prev.stats.messages_today + 1,
            },
            group_streams: streams,
          };
        });
      } else if (data.type === "message.deleted") {
        setOverview((prev) => {
          if (!prev) return prev;
          const streams = prev.group_streams.map((gs) => {
            if (gs.id !== data.group_id) return gs;
            return {
              ...gs,
              messages: gs.messages.map((m) =>
                m.id === data.message_id
                  ? {
                      ...m,
                      deleted: true,
                      text: "",
                      deleted_at: data.deleted_at,
                      deleted_by_role: data.deleted_by_role,
                    }
                  : m
              ),
            };
          });
          return { ...prev, group_streams: streams };
        });
      } else if (data.type === "presence.update") {
        setOverview((prev) =>
          prev
            ? {
                ...prev,
                online_user_ids: data.online_user_ids,
                stats: { ...prev.stats, online_users: data.online_user_ids.length },
              }
            : prev
        );
      }
    });
    wsRef.current = ws;
    return () => {
      try {
        ws.close();
      } catch (_) {}
    };
  }, [token]);

  const handleLogout = () => {
    try {
      wsRef.current?.close();
    } catch (_) {}
    logout();
    navigate("/login", { replace: true });
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.display_name}" (@${u.username})? This cannot be undone.`))
      return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      await Promise.all([loadUsers(), loadOverview()]);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleRenameUser = async (u) => {
    const next = window.prompt(
      `Display name for @${u.username}:`,
      u.display_name || u.username,
    );
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === u.display_name) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { display_name: trimmed });
      await loadUsers();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleToggleAutoJoin = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, {
        auto_join_groups: !u.auto_join_groups,
      });
      await loadUsers();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleToggleOwner = async (u) => {
    if (!window.confirm(
      u.is_owner
        ? `Revoke owner power from ${u.display_name}? They will lose the Force-resend override.`
        : `Grant OWNER power to ${u.display_name}? They will be able to force-resend within the 24-day cooling period.`
    )) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { is_owner: !u.is_owner });
      await loadUsers();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleToggleLedger = async (u) => {
    // No confirm prompt — this is a low-risk, reversible per-user grant.
    // Backend defaults `can_view_ledger` to False so the flip is safe.
    try {
      await api.patch(`/admin/users/${u.id}`, {
        can_view_ledger: !u.can_view_ledger,
      });
      await loadUsers();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  // Set / clear this user's per-team-member complaint sheet webhook URL.
  // When set, complaints owned by this user (matched via staff_name /
  // display_name) are routed to their OWN Google Sheet instead of the
  // global settings sheet. Empty string clears the mapping.
  const handleSetComplaintWebhook = async (u) => {
    const current = u.complaint_sheet_webhook_url || "";
    const next = window.prompt(
      `Complaint sheet webhook URL for ${u.display_name}\n\n` +
      `Complaints owned by this user (matched via staff_name / display_name) will be POSTed to THIS Google Apps Script URL.\n` +
      `Leave blank to clear (falls back to the global sheet).\n\n` +
      `Current: ${current || "(none — using global sheet)"}`,
      current,
    );
    if (next === null) return; // cancelled
    const url = next.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      window.alert("URL must start with http:// or https://");
      return;
    }
    try {
      await api.patch(`/admin/users/${u.id}`, {
        complaint_sheet_webhook_url: url,
      });
      await loadUsers();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const handleDeleteGroup = async (gid) => {
    if (!window.confirm("Delete this group and all its messages?")) return;
    try {
      await api.delete(`/groups/${gid}`);
      await loadOverview();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#F5F5F0] via-[#F0F1EC] to-[#EBE8E0]"
      style={{ fontFamily: "Work Sans, sans-serif" }}
    >
      {/* Top bar */}
      <header className="bg-gradient-to-r from-[#385C4B] via-[#2D4A3C] to-[#385C4B] text-[#F5F5F0] border-b border-[#1F3528] px-4 md:px-6 py-3 md:py-4 sticky top-0 z-20 shadow-md">
        <div className="flex items-center gap-3 md:gap-6 flex-wrap">
          <div
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            data-testid="admin-brand-home"
            title="Home par jaayein"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#F5F5F0]/15 backdrop-blur flex items-center justify-center border border-white/20">
              <Radio className="w-4 h-4 md:w-5 md:h-5 text-[#F5F5F0]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[9px] md:text-[10px] font-mono tracking-[0.25em] uppercase text-[#F5F5F0]/70">
                Admin · Non Stop Chatting
              </div>
              <div
                className="text-base md:text-lg font-semibold tracking-tight leading-none mt-0.5"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                XYZ Chatting
              </div>
            </div>
          </div>

          <nav className="flex items-center gap-1 order-3 md:order-2 md:ml-4 w-full md:w-auto overflow-x-auto">
            {[
              { id: "overview", label: "Overview", icon: Activity },
              { id: "users", label: "Users", icon: Users },
              { id: "groups", label: "Groups", icon: Hash },
              { id: "sheets", label: "Sheets", icon: Sheet },
              { id: "staff", label: "Real Time", icon: Radio },
              { id: "bank", label: "Bank", icon: Landmark },
              { id: "broadcast", label: "Broadcast", icon: Megaphone },
              { id: "telegram", label: "Telegram", icon: Bot },
              { id: "wallet", label: "Wallet", icon: Wallet },
              { id: "backups", label: "Backups", icon: Database },
              { id: "ekyc", label: "EKYC", icon: BadgeCheck },
            ].map((t) => {
              const Ic = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-nav-${t.id}`}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-md text-sm transition-colors flex-shrink-0 ${
                    tab === t.id
                      ? "bg-[#F5F5F0] text-[#385C4B] shadow-sm"
                      : "text-[#F5F5F0]/80 hover:bg-white/10 hover:text-[#F5F5F0]"
                  }`}
                >
                  <Ic className="w-4 h-4" strokeWidth={1.5} />
                  {t.label}
                </button>
              );
            })}
          </nav>

          <div className="flex-1 hidden md:block order-2" />

          <div className="flex items-center gap-1.5 md:gap-2 order-2 md:order-3 ml-auto">
            {/* Primary shortcut group — Home + Chat + Reports + Wallet.
                All routes are one-click from any Admin sub-tab. */}
            <button
              onClick={() => navigate("/home")}
              data-testid="admin-goto-home-button"
              title="Home"
              className="flex items-center gap-1.5 text-xs md:text-sm text-[#F5F5F0]/85 hover:text-[#F5F5F0] transition-colors px-2.5 py-1.5 rounded-md hover:bg-white/10"
            >
              <Home className="w-4 h-4" strokeWidth={1.6} />
              <span className="hidden md:inline">Home</span>
            </button>
            <button
              onClick={() => navigate("/chat")}
              data-testid="admin-goto-chat-button"
              title="Group chat view"
              className="flex items-center gap-1.5 text-xs md:text-sm text-[#F5F5F0]/85 hover:text-[#F5F5F0] transition-colors px-2.5 py-1.5 rounded-md hover:bg-white/10"
            >
              <MessageSquare className="w-4 h-4" strokeWidth={1.6} />
              <span className="hidden lg:inline">Chat</span>
            </button>
            <button
              onClick={() => navigate("/lpg-report")}
              data-testid="admin-goto-lpg-report-button"
              title="LPG delivery report"
              className="flex items-center gap-1.5 text-xs md:text-sm text-[#F5F5F0]/85 hover:text-[#F5F5F0] transition-colors px-2.5 py-1.5 rounded-md hover:bg-white/10"
            >
              <Flame className="w-4 h-4" strokeWidth={1.6} />
              <span className="hidden lg:inline">LPG Report</span>
            </button>
            <button
              onClick={() => navigate("/admin/wallet")}
              data-testid="admin-goto-wallet-button"
              title="Wallet & ledger"
              className="flex items-center gap-1.5 text-xs md:text-sm text-[#F5F5F0]/85 hover:text-[#F5F5F0] transition-colors px-2.5 py-1.5 rounded-md hover:bg-white/10"
            >
              <Wallet className="w-4 h-4" strokeWidth={1.6} />
              <span className="hidden lg:inline">Wallet</span>
            </button>
            <button
              onClick={() => navigate("/complaints")}
              data-testid="admin-goto-complaints-button"
              title="Complaints inbox"
              className="relative flex items-center gap-1.5 text-xs md:text-sm text-[#F5F5F0]/85 hover:text-[#F5F5F0] transition-colors px-2.5 py-1.5 rounded-md hover:bg-white/10"
            >
              <Inbox className="w-4 h-4" strokeWidth={1.6} />
              <span className="hidden lg:inline">Complaints</span>
              {complaintCount > 0 && (
                <span
                  data-testid="admin-complaint-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8553D] text-white text-[10px] font-bold flex items-center justify-center shadow"
                >
                  {complaintCount > 99 ? "99+" : complaintCount}
                </span>
              )}
            </button>
            <div className="hidden md:block h-6 w-px bg-white/25 mx-1" />
            <div className="text-right hidden sm:block leading-tight">
              <div
                className="text-xs md:text-sm font-semibold text-[#F5F5F0]"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {user?.display_name}
              </div>
              <div className="text-[9px] md:text-[10px] font-mono tracking-[0.2em] uppercase text-[#F5F5F0]/70">
                {user?.role}
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              title="Logout"
              className="p-2 rounded-md hover:bg-white/15 text-[#F5F5F0]/85 hover:text-[#F5F5F0] transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="px-4 md:px-6 pt-4">
          <div className="bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] text-sm px-4 py-2 rounded-md flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError("")} className="text-xs underline">
              dismiss
            </button>
          </div>
        </div>
      )}

      <main className="p-4 md:p-6">
        {tab === "overview" && overview && (
          <OverviewTab overview={overview} onDeleteGroup={handleDeleteGroup} />
        )}
        {tab === "users" && (
          <UsersTab
            users={users}
            onReloadUsers={loadUsers}
            onlineIds={overview?.online_user_ids || []}
            onCreate={() => setShowCreateUser(true)}
            onDelete={handleDeleteUser}
            onRename={handleRenameUser}
            onResetPw={(u) => setShowResetPw(u)}
            onToggleAutoJoin={handleToggleAutoJoin}
            onToggleOwner={handleToggleOwner}
            onToggleLedger={handleToggleLedger}
            onSetComplaintWebhook={handleSetComplaintWebhook}
            currentUserId={user?.id}
            currentIsOwner={!!user?.is_owner}
          />
        )}
        {tab === "backups" && <BackupsTab />}
        {tab === "sheets" && <SheetsTab />}
        {tab === "staff" && <StaffSheetsTab />}
        {tab === "groups" && <GroupsTab />}
        {tab === "ekyc" && <EkycAdmin />}
        {tab === "bank" && <BankAccountManager />}
        {tab === "broadcast" && <BroadcastNoticeManager />}
        {tab === "telegram" && <TelegramAlertsManager />}
        {tab === "wallet" && (
          <div data-testid="admin-wallet-embedded" className="-mx-4 sm:-mx-6 lg:-mx-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <WalletPage embedded />
            </div>
          </div>
        )}
      </main>

      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreated={() => {
            setShowCreateUser(false);
            loadUsers();
            loadOverview();
          }}
        />
      )}

      {showResetPw && (
        <ResetPasswordModal
          user={showResetPw}
          onClose={() => setShowResetPw(null)}
          onDone={() => setShowResetPw(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, testid, accent = "#385C4B" }) {
  return (
    <div
      className="relative bg-white border border-[#DCDCD3] rounded-lg p-4 md:p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg overflow-hidden group"
      data-testid={testid}
    >
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: accent }}
      />
      <div className="text-[10px] font-mono tracking-[0.2em] md:tracking-[0.25em] uppercase text-[#6B6B61] mb-2 md:mb-3">
        {label}
      </div>
      <div
        className="font-mono text-2xl md:text-4xl tracking-tight"
        style={{ fontFamily: "Inter, sans-serif", color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function DedupWindowCard() {
  const [days, setDays] = useState(24);
  const [saved, setSaved] = useState(24);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/dedup-window");
        setDays(data.days);
        setSaved(data.days);
      } catch (e) {
        setMsg({ type: "error", text: formatApiError(e) });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    if (days < 1 || days > 90) {
      setMsg({ type: "error", text: "Days must be between 1 and 90" });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.put("/admin/settings/dedup-window", { days: parseInt(days, 10) });
      setSaved(days);
      setMsg({ type: "ok", text: `Cooling window updated to ${days} days.` });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#DCDCD3] rounded-lg p-5 mb-6"
      data-testid="dedup-window-card"
    >
      <div className="flex items-baseline gap-2 mb-1.5">
        <span>♻️</span>
        <h3
          className="text-lg font-semibold text-[#1C1C19]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Duplicate Cooling Window
        </h3>
        <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#385C4B]">
          Global
        </span>
      </div>
      <p className="text-xs text-[#6B6B61] mt-1 leading-relaxed">
        Kitne <b>din tak</b> ek LPG ID cooling me rahega — repeat dispatch block
        hoga is window ke andar. Default <b>24 din</b>. Aap apne agency cycle ke
        hisab se badha/ghata sakte hain (1..90 din).
      </p>
      <div className="mt-2 text-[11px] text-[#8A6046] leading-relaxed">
        <b>Ye setting kaha kaha lagta hai:</b>
        <ul className="list-disc list-inside mt-0.5 space-y-0.5 text-[#6B6B61]">
          <li>
            <b>Chat me single LPG paste</b> — same ID {saved} din ke andar
            dobara aayegi to “already dispatched” error milega.
          </li>
          <li>
            <b>Bulk Paste dedup pre-check</b> — {saved} din wale duplicates
            auto-remove ho jate hain (green tick, no re-dispatch).
          </li>
          <li>
            <b>Bulk Resend / Force Send</b> — {saved} din se puraane IDs cooling
            se bahar → fresh dispatch allowed. Owner bypass bhi is window pe
            based hai.
          </li>
          <li>
            <b>LPG Report duplicate flagging</b> — same window follow karta hai.
          </li>
        </ul>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <label
          htmlFor="dedup-days-input"
          className="text-xs text-[#6B6B61]"
        >
          Cooling window (days):
        </label>
        <input
          id="dedup-days-input"
          type="number"
          min={1}
          max={90}
          value={days}
          onChange={(e) => setDays(e.target.value ? parseInt(e.target.value, 10) : "")}
          disabled={loading}
          data-testid="dedup-days-input"
          className="w-20 px-2 py-1 border border-[#DCDCD3] rounded text-sm text-center font-mono tabular-nums focus:outline-none focus:border-[#385C4B]"
        />
        <button
          onClick={save}
          disabled={busy || loading || days === saved}
          data-testid="dedup-days-save"
          className="px-3 py-1 text-xs font-mono rounded bg-[#385C4B] text-white hover:bg-[#2D4A3C] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Saving…" : days === saved ? "Saved" : "Save"}
        </button>
        <span className="text-[11px] font-mono text-[#6B6B61]">
          Current: <b className="text-[#1C1C19]">{saved} days</b>
        </span>
      </div>
      {msg && (
        <div
          className={`mt-3 p-2 rounded text-xs ${
            msg.type === "ok"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}


function OverviewTab({ overview, onDeleteGroup }) {
  const { stats, group_streams } = overview;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
        <StatCard label="Total Users" value={stats.total_users} testid="admin-stat-card" accent="#385C4B" />
        <StatCard label="Groups" value={stats.total_groups} testid="admin-stat-card" accent="#3E7FA0" />
        <StatCard label="Messages Today" value={stats.messages_today} testid="admin-stat-card" accent="#C8835A" />
        <StatCard label="Total Messages" value={stats.total_messages} testid="admin-stat-card" accent="#7B6BA1" />
        <StatCard label="Online Now" value={stats.online_users} testid="admin-stat-card" accent="#5C8B6E" />
      </div>

      <DedupWindowCard />

      <div className="mb-5 flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61]">
            Live Monitoring
          </div>
          <h2
            className="text-2xl font-semibold tracking-tight text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Group Streams
          </h2>
        </div>
        <div className="text-xs font-mono text-[#385C4B] flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#385C4B] animate-pulse" />
          LIVE
        </div>
      </div>

      {group_streams.length === 0 ? (
        <div className="bg-white border border-[#DCDCD3] rounded-md p-12 text-center">
          <MessageCircle
            className="w-10 h-10 text-[#385C4B]/30 mx-auto mb-3"
            strokeWidth={1.25}
          />
          <div className="text-sm text-[#6B6B61]">
            No groups yet. Create one from the Chat view.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {group_streams.map((g) => (
            <GroupStreamCard key={g.id} group={g} onDelete={() => onDeleteGroup(g.id)} />
          ))}
        </div>
      )}
    </>
  );
}

function GroupStreamCard({ group, onDelete }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [group.messages.length]);

  return (
    <div
      className="bg-white border border-[#DCDCD3] rounded-md flex flex-col h-[420px] overflow-hidden transition-all hover:border-[#385C4B]/40"
      data-testid="admin-group-stream-card"
    >
      <div className="px-4 py-3 border-b border-[#DCDCD3] bg-[#EBEBE6] flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-[#385C4B]/15 border border-[#385C4B]/25 flex items-center justify-center text-[#385C4B] font-semibold text-xs">
          {(group.name[0] || "?").toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-semibold text-[#1C1C19] truncate tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {group.name}
          </div>
          <div className="text-[10px] font-mono text-[#6B6B61] uppercase tracking-wider">
            {group.member_count} members · {group.messages.length} recent
          </div>
        </div>
        <button
          onClick={onDelete}
          className="p-1.5 rounded-md text-[#6B6B61] hover:text-[#8A6046] hover:bg-[#8A6046]/10 transition-colors"
          title="Delete group"
          data-testid="admin-delete-group-button"
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F5F5F0]">
        {group.messages.length === 0 ? (
          <div className="text-center text-xs text-[#6B6B61] py-8">No messages yet</div>
        ) : (
          group.messages.map((m) =>
            m.deleted ? (
              <div
                key={m.id}
                className="bg-[#FFF8F1] border border-dashed border-[#8A6046]/40 rounded-md p-2.5"
                data-testid="admin-stream-deleted-msg"
              >
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div
                    className="text-xs font-semibold text-[#8A6046]"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {m.sender_display_name}
                  </div>
                  <div
                    className="text-[10px] font-mono text-[#6B6B61]"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {formatTime(m.created_at)}
                  </div>
                </div>
                <div className="text-xs text-[#1C1C19] break-words whitespace-pre-wrap line-through opacity-75">
                  {m.text}
                </div>
                <div
                  className="mt-1.5 flex items-center gap-1.5 text-[10px] font-mono text-[#8A6046]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                  DELETED
                  {m.deleted_by_role
                    ? ` BY ${m.deleted_by_role.toUpperCase()}`
                    : ""}
                </div>
              </div>
            ) : (
              <div key={m.id} className="bg-white border border-[#DCDCD3] rounded-md p-2.5">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <div
                  className="text-xs font-semibold text-[#385C4B]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {m.sender_display_name}
                </div>
                <div
                  className="text-[10px] font-mono text-[#6B6B61]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {formatTime(m.created_at)}
                </div>
              </div>
              <div className="text-xs text-[#1C1C19] break-words whitespace-pre-wrap">
                {m.text}
              </div>
            </div>
            )
          )
        )}
      </div>
    </div>
  );
}

function UsersTab({ users, onReloadUsers, onlineIds, onCreate, onDelete, onRename, onResetPw, onToggleAutoJoin, onToggleOwner, onToggleLedger, onSetComplaintWebhook, currentUserId, currentIsOwner }) {
  const online = new Set(onlineIds);
  // Search + role filter — admin 549 users mein se specific user dhundhne ke
  // liye. Client-side (users already loaded), case-insensitive, matches
  // display_name / username / role.
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && (u.role || "member") !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.display_name || "").toLowerCase().includes(q) ||
        (u.username || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q)
      );
    });
  }, [users, search, roleFilter]);
  const { sorted: sortedUsers, label: sortLabel, cycle: cycleSort } = useNameSort(
    filteredUsers,
    "users",
    (u) => String(u?.display_name || u?.username || "").toUpperCase()
  );
  const isFiltering = search.trim() || roleFilter !== "all";
  // Role chips — count each role so admin knows the breakdown at a glance.
  const roleCounts = useMemo(() => {
    const c = { all: users.length, admin: 0, resend_staff: 0, team_staff: 0, ekyc_staff: 0, delivery_man: 0, booker: 0, salesman: 0, developer: 0, member: 0 };
    for (const u of users) {
      const r = u.role || "member";
      if (c[r] !== undefined) c[r] += 1;
    }
    return c;
  }, [users]);
  const roleChips = [
    { key: "all",          label: "All" },
    { key: "admin",        label: "Admin" },
    { key: "resend_staff", label: "Resend" },
    { key: "team_staff",   label: "Team" },
    { key: "ekyc_staff",   label: "EKYC" },
    { key: "delivery_man", label: "Delivery" },
    { key: "booker",       label: "Booker" },
    { key: "salesman",     label: "Salesman" },
    { key: "developer",    label: "Developer" },
    { key: "member",       label: "Member" },
  ];

  return (
    <>
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61]">
            Directory
          </div>
          <h2
            className="text-2xl font-semibold tracking-tight text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Users · {users.length}
            {isFiltering && (
              <span className="text-sm font-normal text-[#6B6B61] ml-2" data-testid="admin-users-filtered-count">
                · Showing {filteredUsers.length}
              </span>
            )}
          </h2>
          <p className="text-xs text-[#6B6B61] mt-1 flex items-center gap-1.5">
            <Star className="w-3 h-3 text-[#b45309]" strokeWidth={2} fill="currentColor" />
            Mark users as <span className="font-semibold text-[#b45309]">Selected</span> to auto-add them in every new group.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={cycleSort}
            data-testid="admin-users-sort-toggle"
            title="Sort order — click to cycle (A-Z / Z-A / Newest)"
            className="bg-white border border-[#DCDCD3] hover:border-[#385C4B] text-[#385C4B] px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-xs font-mono tracking-[0.1em] uppercase"
          >
            <ArrowUpDown className="w-3.5 h-3.5" strokeWidth={1.75} />
            {sortLabel}
          </button>
          <button
            onClick={onCreate}
            data-testid="admin-create-user-button"
            className="bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] px-4 py-2.5 rounded-md transition-colors flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" strokeWidth={1.75} />
            New User
          </button>
        </div>
      </div>

      {/* Search + role filter chips */}
      <div className="bg-white border border-[#DCDCD3] rounded-lg px-3 py-2.5 mb-3 flex flex-col sm:flex-row sm:items-center gap-2 shadow-sm">
        <div className="flex-1 relative min-w-0">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B6B61]" strokeWidth={1.75} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name / username / role…"
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white focus:outline-none focus:border-[#385C4B]"
            data-testid="admin-users-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6B6B61] hover:text-[#1C1C19] p-0.5"
              data-testid="admin-users-search-clear"
              title="Clear search"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="admin-users-role-chips">
          {roleChips.map((c) => {
            const active = roleFilter === c.key;
            const n = roleCounts[c.key] ?? 0;
            return (
              <button
                key={c.key}
                onClick={() => setRoleFilter(c.key)}
                className={`flex-shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-1.5 rounded border transition-colors ${
                  active
                    ? "bg-[#385C4B] text-white border-[#385C4B]"
                    : "bg-white text-[#6B6B61] border-[#DCDCD3] hover:border-[#385C4B]"
                }`}
                data-testid={`admin-users-role-chip-${c.key}`}
              >
                {c.label} <span className={active ? "opacity-80" : "opacity-60"}>({n})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
          <thead>
            <tr className="bg-[#EBEBE6] text-left">
              {["User", "Username", "Role", "Status", "Auto-Join", "Owner", "Created", "Actions"].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-[#6B6B61]" data-testid="admin-users-empty">
                  <Search className="w-6 h-6 inline mb-2 opacity-40" strokeWidth={1.75} /><br />
                  No users match {search ? `"${search}"` : "this filter"}.
                  <button
                    onClick={() => { setSearch(""); setRoleFilter("all"); }}
                    className="ml-2 text-[#385C4B] underline hover:text-[#2D4A3C]"
                    data-testid="admin-users-clear-filters"
                  >
                    Clear filters
                  </button>
                </td>
              </tr>
            )}
            {sortedUsers.map((u) => (
              <tr
                key={u.id}
                className="border-t border-[#DCDCD3] hover:bg-[#F5F5F0]/60"
                data-testid="admin-user-row"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div
                      className="text-sm font-semibold text-[#1C1C19]"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {u.display_name}
                    </div>
                    <button
                      onClick={() => onRename(u)}
                      data-testid="admin-user-rename-button"
                      title="Rename display name"
                      className="text-[#6B6B61] hover:text-[#385C4B] p-1 rounded hover:bg-[#385C4B]/10"
                    >
                      <Pencil className="w-3 h-3" strokeWidth={1.75} />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#6B6B61]">@{u.username}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded ${
                      u.role === "admin"
                        ? "bg-[#385C4B] text-[#F5F5F0]"
                        : "bg-[#EBEBE6] text-[#6B6B61]"
                    }`}
                  >
                    {u.role}
                  </span>
                  {u.role === "delivery_man" && <DeliveryAgenciesButton user={u} onSaved={onReloadUsers} />}
                </td>
                <td className="px-4 py-3">
                  {online.has(u.id) ? (
                    <span className="text-xs text-[#385C4B] flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#385C4B] animate-pulse" />
                      Online
                    </span>
                  ) : (
                    <span className="text-xs text-[#6B6B61]">Offline</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded bg-[#385C4B] text-[#F5F5F0]"
                      title="Admins are automatically added to every group"
                      data-testid="admin-autojoin-always"
                    >
                      <Star className="w-3 h-3" strokeWidth={2} fill="currentColor" />
                      Always
                    </span>
                  ) : (
                    <button
                      onClick={() => onToggleAutoJoin(u)}
                      data-testid={
                        u.auto_join_groups
                          ? "admin-autojoin-selected-button"
                          : "admin-autojoin-select-button"
                      }
                      className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md border transition-all ${
                        u.auto_join_groups
                          ? "bg-[#fef3c7] border-[#b45309] text-[#b45309] hover:bg-[#fde68a] shadow-sm"
                          : "bg-white border-[#DCDCD3] text-[#6B6B61] hover:border-[#b45309] hover:text-[#b45309] hover:bg-[#fef3c7]/40"
                      }`}
                      title={
                        u.auto_join_groups
                          ? "Selected · auto-joins every new group. Click to remove."
                          : "Click to auto-add this user to every new group"
                      }
                    >
                      <Star
                        className="w-3.5 h-3.5"
                        strokeWidth={1.75}
                        fill={u.auto_join_groups ? "currentColor" : "none"}
                      />
                      {u.auto_join_groups ? "Selected" : "Select"}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {/* Owner badge — only admins are eligible. Owners can grant /
                      revoke ownership. Everyone else sees a static badge. */}
                  {u.role !== "admin" ? (
                    <span className="text-[10px] text-[#6B6B61] font-mono">—</span>
                  ) : u.is_owner ? (
                    currentIsOwner ? (
                      <button
                        onClick={() => onToggleOwner(u)}
                        data-testid="admin-owner-revoke-button"
                        title="Owner · click to revoke (only owners can change this)"
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-[#385C4B] text-[#F5F5F0] border border-[#385C4B] hover:bg-[#2D4A3C] transition-colors"
                      >
                        <KeyRound className="w-3.5 h-3.5" strokeWidth={2} />
                        Owner
                      </button>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-[#385C4B] text-[#F5F5F0]"
                        data-testid="admin-owner-badge"
                      >
                        <KeyRound className="w-3.5 h-3.5" strokeWidth={2} />
                        Owner
                      </span>
                    )
                  ) : currentIsOwner ? (
                    <button
                      onClick={() => onToggleOwner(u)}
                      data-testid="admin-owner-grant-button"
                      title="Grant owner power (force-resend override)"
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-white border border-[#DCDCD3] text-[#6B6B61] hover:border-[#385C4B] hover:text-[#385C4B] transition-colors"
                    >
                      <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                      Grant
                    </button>
                  ) : (
                    <span className="text-[10px] text-[#6B6B61] font-mono">—</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[#6B6B61]">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {/* Ledger access toggle — admin controls whether this
                        user sees the My Agency Ledger tile. Admin-role
                        users are hidden (they already see full wallet). */}
                    {u.role !== "admin" && u.role !== "member" && (
                      <button
                        onClick={() => onToggleLedger(u)}
                        data-testid={
                          u.can_view_ledger
                            ? `admin-ledger-revoke-${u.id}`
                            : `admin-ledger-grant-${u.id}`
                        }
                        className={`p-1.5 rounded-md transition-colors ${
                          u.can_view_ledger
                            ? "text-[#385C4B] bg-[#385C4B]/10 hover:bg-[#385C4B]/20"
                            : "text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#385C4B]/10"
                        }`}
                        title={
                          u.can_view_ledger
                            ? "Ledger enabled · click to revoke"
                            : "Grant ledger access (user will see My Agency Ledger tile)"
                        }
                      >
                        <Wallet
                          className="w-3.5 h-3.5"
                          strokeWidth={u.can_view_ledger ? 2.25 : 1.5}
                        />
                      </button>
                    )}
                    <button
                      onClick={() => onSetComplaintWebhook(u)}
                      data-testid={`admin-complaint-webhook-${u.id}`}
                      className={`p-1.5 rounded-md transition-colors ${
                        u.complaint_sheet_webhook_url
                          ? "text-[#7A5A1F] bg-[#F0DFA5]/50 hover:bg-[#F0DFA5]/80"
                          : "text-[#6B6B61] hover:text-[#7A5A1F] hover:bg-[#F0DFA5]/30"
                      }`}
                      title={
                        u.complaint_sheet_webhook_url
                          ? `Complaint sheet: routed to own sheet · click to change`
                          : "Set per-team-member complaint Google Sheet webhook URL"
                      }
                    >
                      <FileSpreadsheet
                        className="w-3.5 h-3.5"
                        strokeWidth={u.complaint_sheet_webhook_url ? 2.25 : 1.5}
                      />
                    </button>
                    <button
                      onClick={() => onResetPw(u)}
                      data-testid="admin-reset-password-button"
                      className="p-1.5 rounded-md text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors"
                      title="Reset password"
                    >
                      <Key className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    {u.id !== currentUserId && (
                      <button
                        onClick={() => onDelete(u)}
                        data-testid="admin-delete-user-button"
                        className="p-1.5 rounded-md text-[#6B6B61] hover:text-[#8A6046] hover:bg-[#8A6046]/10 transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("member");
  const [resendTeam, setResendTeam] = useState("");
  const [staffName, setStaffName] = useState("");
  // For role='team_staff' — comma-separated list of resend_team labels this
  // lead oversees. Sent as an array to the backend.
  const [linkedTeams, setLinkedTeams] = useState("");
  const [deliveryAgencies, setDeliveryAgencies] = useState([]);
  // Auto-create a group with the same name (agency workflow). Default ON
  // for member/team_staff since almost every new agency needs a group.
  const [autoCreateGroup, setAutoCreateGroup] = useState(true);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/admin/users", {
        username: username.trim().toLowerCase(),
        display_name: displayName.trim(),
        password,
        role,
        resend_team: role === "resend_staff" ? resendTeam.trim() : "",
        staff_name: role === "team_staff" ? staffName.trim() : "",
        linked_resend_teams:
          role === "team_staff"
            ? linkedTeams
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        auto_create_group: role === "member" && autoCreateGroup,
        delivery_agency_ids: role === "delivery_man" ? deliveryAgencies : [],
      });
      onCreated();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setBulkResult(null);
    const lines = bulkText
      .replace(/\r/g, "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length < 2) {
      setError("Need a header row + at least one data row.");
      setSaving(false);
      return;
    }
    const sep = lines[0].includes("\t") ? "\t" : ",";
    const header = lines[0].split(sep).map((h) => h.trim().toLowerCase());
    for (const r of ["username", "password", "display_name"]) {
      if (!header.includes(r)) {
        setError(`Header missing column "${r}"`);
        setSaving(false);
        return;
      }
    }
    const users = lines.slice(1).map((row) => {
      const cells = row.split(sep);
      const get = (k) => (cells[header.indexOf(k)] || "").trim();
      return {
        username: get("username"),
        password: get("password"),
        display_name: get("display_name"),
        resend_team: header.includes("resend_team") ? get("resend_team") : "",
      };
    });
    try {
      const { data } = await api.post("/admin/users/bulk", { users });
      setBulkResult(data);
      if ((data.created || []).length > 0) onCreated();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="admin-create-user-modal"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-2xl font-semibold tracking-tight text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {bulkMode ? "Bulk add Resend Staff" : "New User"}
          </h2>
          <button
            type="button"
            onClick={() => {
              setBulkMode(!bulkMode);
              setError("");
              setBulkResult(null);
            }}
            data-testid="admin-create-user-toggle-bulk"
            className="text-[10px] font-mono tracking-[0.15em] uppercase px-3 py-1.5 border border-[#DCDCD3] rounded hover:bg-[#F5F5F0]"
          >
            {bulkMode ? "Single mode" : "Bulk mode"}
          </button>
        </div>
        {bulkMode ? (
          <form onSubmit={submitBulk} className="space-y-3">
            <p className="text-xs text-[#6B6B61]">
              Paste CSV/TSV. Header row required: <code className="bg-[#F5F5F0] px-1 rounded">username</code>,{" "}
              <code className="bg-[#F5F5F0] px-1 rounded">password</code>,{" "}
              <code className="bg-[#F5F5F0] px-1 rounded">display_name</code>,{" "}
              optional <code className="bg-[#F5F5F0] px-1 rounded">resend_team</code>.
              All rows are created as <strong>resend_staff</strong>.
            </p>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={10}
              required
              data-testid="admin-create-user-bulk-textarea"
              placeholder={`username,password,display_name,resend_team\nravi.r,Ravi@1234,Ravi Kumar,Vicky Team\npriya.r,Priya@1234,Priya Singh,Vicky Team`}
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B] font-mono text-xs"
            />
            {bulkResult && (
              <div
                data-testid="admin-create-user-bulk-result"
                className="text-xs font-mono bg-[#F5F5F0] border border-[#DCDCD3] rounded p-2 space-y-1"
              >
                <div className="text-emerald-700">✓ Created: {(bulkResult.created || []).length}</div>
                {(bulkResult.skipped || []).length > 0 && (
                  <div className="text-[#8A6046]">
                    ⚠ Skipped: {bulkResult.skipped.length}
                    <ul className="ml-3 mt-1 list-disc">
                      {bulkResult.skipped.map((s, i) => (
                        <li key={i}>{s.username}: {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {error && (
              <div className="text-sm text-[#8A6046] bg-[#8A6046]/10 border border-[#8A6046]/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#DCDCD3] rounded-md hover:bg-[#F5F5F0]">
                Close
              </button>
              <button
                type="submit"
                disabled={saving || !bulkText.trim()}
                data-testid="admin-create-user-bulk-submit"
                className="flex-1 px-4 py-2.5 bg-[#385C4B] text-white rounded-md hover:bg-[#2d4a3c] disabled:opacity-50"
              >
                {saving ? "Creating…" : "Bulk create"}
              </button>
            </div>
          </form>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Display name">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              data-testid="create-user-displayname-input"
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B]"
            />
          </Field>
          <Field label="Username (for login)">
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              pattern="[a-zA-Z0-9._\-]{3,}"
              data-testid="create-user-username-input"
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B] font-mono"
              placeholder="e.g. rahul.k"
            />
          </Field>
          <Field label="Password (min 6 chars)">
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              data-testid="create-user-password-input"
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B] font-mono"
            />
          </Field>
          <Field label="Role">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="create-user-role-select"
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B] bg-white"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              <option value="resend_staff">Resend Staff (locked to Resend Desk only)</option>
              <option value="team_staff">Team Staff (own ledger + linked resend teams)</option>
              <option value="ekyc_staff">EKYC Staff (locked to EKYC Desk only)</option>
              <option value="delivery_man">Delivery Man (locked to Pending Deliveries only)</option>
              <option value="booker">Booker (sirf apna payout hisab)</option>
              <option value="salesman">Salesman (sirf apna payout hisab)</option>
              <option value="developer">Developer (sirf apna payout hisab)</option>
            </select>
          </Field>
          {role === "resend_staff" && (
            <Field label="Resend Team (optional label)">
              <input
                value={resendTeam}
                onChange={(e) => setResendTeam(e.target.value)}
                maxLength={60}
                placeholder="e.g. Vicky Team"
                data-testid="create-user-team-input"
                className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B]"
              />
            </Field>
          )}
          {role === "delivery_man" && (
            <Field label="Agencies (is delivery man ko sirf inki pending deliveries dikhengi)">
              <DeliveryAgencyPicker value={deliveryAgencies} onChange={setDeliveryAgencies} />
            </Field>
          )}
          {role === "team_staff" && (
            <Field label="Staff Name (must EXACTLY match 'Uploaded By' name)">
              <input
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                maxLength={120}
                required
                placeholder="e.g. ASHISH  or  Ravi · Booking Team"
                data-testid="create-user-staff-name"
                className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B]"
              />
              <p className="text-[11px] text-[#6B6B61] mt-1">
                Case-insensitive match. Wallet & Ledger will show this user
                only their own uploads under this name.
              </p>
            </Field>
          )}
          {role === "team_staff" && (
            <Field label="Linked Resend Teams (comma-separated, optional)">
              <input
                value={linkedTeams}
                onChange={(e) => setLinkedTeams(e.target.value)}
                maxLength={400}
                placeholder="e.g. ASIT, ASHISH, JITENDRA"
                data-testid="create-user-linked-teams"
                className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B] font-mono text-sm"
              />
              <p className="text-[11px] text-[#6B6B61] mt-1">
                Team lead will see the same 4-column resend board as admin,
                scoped to entries created by these <code>resend_team</code>{" "}
                labels. Leave blank to give no resend visibility.
              </p>
            </Field>
          )}
          {role === "member" && (
            <div className="flex items-start gap-2.5 bg-[#F5F5F0] border border-[#DCDCD3] rounded-md px-3 py-2.5">
              <input
                id="auto-create-group-cb"
                type="checkbox"
                checked={autoCreateGroup}
                onChange={(e) => setAutoCreateGroup(e.target.checked)}
                data-testid="create-user-auto-group-checkbox"
                className="mt-0.5 h-4 w-4 accent-[#385C4B] cursor-pointer"
              />
              <label
                htmlFor="auto-create-group-cb"
                className="text-sm text-[#1C1C19] cursor-pointer select-none"
              >
                Auto-create group with same name as{" "}
                <span className="font-medium">
                  {(displayName || "display name").trim() || "display name"}
                </span>
                <p className="text-[11px] text-[#6B6B61] mt-0.5 font-normal">
                  Agency workflow · Admin(s) + this member honge. Warning: agar
                  same naam ka group already hai to user create nahi hoga.
                </p>
              </label>
            </div>
          )}
          {error && (
            <div className="text-sm text-[#8A6046] bg-[#8A6046]/10 border border-[#8A6046]/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-[#6B6B61] hover:text-[#1C1C19] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="create-user-submit"
              className="bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] px-5 py-2 rounded-md transition-colors disabled:opacity-60"
            >
              {saving ? "Creating…" : "Create User"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pw });
      alert(`Password reset for @${user.username}. Share with user.`);
      onDone();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="admin-reset-password-modal"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          className="text-xl font-semibold tracking-tight text-[#1C1C19] mb-2"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Reset Password
        </h2>
        <p className="text-sm text-[#6B6B61] mb-5 font-mono">
          @{user.username} · {user.display_name}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <Field label="New password (min 6)">
            <input
              type="text"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={6}
              data-testid="reset-password-input"
              className="w-full border border-[#DCDCD3] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#385C4B] focus:border-[#385C4B] font-mono"
            />
          </Field>
          {error && (
            <div className="text-sm text-[#8A6046] bg-[#8A6046]/10 border border-[#8A6046]/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-[#6B6B61] hover:text-[#1C1C19] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="reset-password-submit"
              className="bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] px-5 py-2 rounded-md transition-colors disabled:opacity-60"
            >
              {saving ? "Saving…" : "Reset"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block">
        {label}
      </label>
      {children}
    </div>
  );
}

function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

function formatBackupTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function BackupsTab() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setBackups(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runNow = async () => {
    setRunning(true);
    setError("");
    try {
      await api.post("/admin/backups/run");
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const download = async (b) => {
    try {
      const token = localStorage.getItem("gc_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${b.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Download failed");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete backup "${b.filename}"?`)) return;
    try {
      await api.delete(`/admin/backups/${b.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61]">
            Data Vault
          </div>
          <h2
            className="text-2xl font-semibold tracking-tight text-[#1C1C19]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Backups · {backups.length}
          </h2>
          <div className="text-xs text-[#6B6B61] mt-1">
            Auto-runs daily at 02:00 UTC · last 60 retained
          </div>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          data-testid="admin-run-backup-button"
          className="bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] px-4 py-2.5 rounded-md transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
        >
          {running ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <PlayCircle className="w-4 h-4" strokeWidth={1.75} />
          )}
          {running ? "Running…" : "Run Backup Now"}
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] text-sm px-4 py-2 rounded-md">
          {error}
        </div>
      )}

      {backups.length === 0 ? (
        <div className="bg-white border border-[#DCDCD3] rounded-md p-12 text-center shadow-sm">
          <Database className="w-10 h-10 text-[#385C4B]/30 mx-auto mb-3" strokeWidth={1.25} />
          <div className="text-sm text-[#6B6B61]">
            No backups yet. Click <span className="font-semibold">Run Backup Now</span> to create
            your first snapshot.
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-[#EBEBE6] text-left">
                  {["Created", "Filename", "Type", "Size", "Records", "Actions"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr
                    key={b.id}
                    className="border-t border-[#DCDCD3] hover:bg-[#F5F5F0]/60"
                    data-testid="admin-backup-row"
                  >
                    <td className="px-4 py-3">
                      <div
                        className="text-sm font-semibold text-[#1C1C19]"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {formatBackupTime(b.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#6B6B61] break-all">
                      {b.filename}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-1 rounded ${
                          b.reason === "scheduled"
                            ? "bg-[#385C4B] text-[#F5F5F0]"
                            : "bg-[#C8835A] text-[#F5F5F0]"
                        }`}
                      >
                        {b.reason}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#1C1C19]">
                      {formatBytes(b.size_bytes)}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B6B61]">
                      {b.counts ? (
                        <>
                          {b.counts.users}u · {b.counts.groups}g · {b.counts.messages}m
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => download(b)}
                          data-testid="admin-download-backup-button"
                          className="p-1.5 rounded-md text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors"
                          title="Download JSON"
                        >
                          <Download className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => remove(b)}
                          data-testid="admin-delete-backup-button"
                          className="p-1.5 rounded-md text-[#6B6B61] hover:text-[#8A6046] hover:bg-[#8A6046]/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}


// =============================================================================
// Sheets Tab — Google Sheets sync for LPG master data
// =============================================================================
const APPS_SCRIPT_SNIPPET = `/**
 * XYZ Chatting · LPG Daily Report sync (v5 · Iter88 · Sheet2 Refresh Fix)
 *
 * Sheet1 (Daily Aggregate) — one row per (Date, Agency). Existing v3 behaviour
 *   preserved 100%: chronological sort, blank row on date change, user remarks
 *   in columns J..Q survive across syncs.
 *
 * Sheet2 (Per-Batch Log) — one row per LPG upload event. Same agency 5x in a
 *   day → 5 separate rows with IST time so admins see each batch individually.
 *   Blank separator row on date change. Remarks reserved in columns L..T
 *   are preserved across syncs. \`deleted=true\` rows remove the batch line.
 *   Sheet2 tab auto-created on first sync — no manual setup needed.
 *   v5 (Iter88): "Refresh" button (action='backfill') now wipes Sheet2 rows
 *   whose unique_key is NOT in the incoming payload — i.e. deleted messages
 *   / deleted groups / test fixtures vanish automatically. Same semantics
 *   as Sheet1's backfill so both tabs stay in perfect sync with live DB.
 *
 * Paste in Extensions → Apps Script → Save → Deploy → Web app → Anyone.
 * Same webhook URL as before — no redeploy needed if you keep the deployment.
 */
const HEADER = [
  "Date","Agency","Sent","Done","Pending","Billable","Team Members","Last Updated","Unique Key"
];
const REMARKS_COLS = 8;  // columns J..Q reserved for user-typed remarks (Sheet1)

const HEADER_BATCH = [
  "Date","Time (IST)","Agency","Sent","Done","Pending","Billable",
  "Team Members","Batch ID","Last Updated","Unique Key"
];
const REMARKS_COLS_BATCH = 9;  // columns L..T reserved for remarks (Sheet2)

function doPost(e) {
  const lock = LockService.getScriptLock();
  lock.waitLock(20000);
  try {
    const body    = JSON.parse(e.postData.contents);
    const incoming    = body.rows || [];
    const incomingBatch = body.batch_rows || [];
    const action  = body.action || "upsert";
    const ss      = SpreadsheetApp.getActiveSpreadsheet();

    // ───────────────────────────────────────────────────────────────
    // Sheet1 — Daily Aggregate (existing v3 behaviour, unchanged)
    // ───────────────────────────────────────────────────────────────
    const sheet = ss.getSheets()[0];
    if (sheet.getLastRow() < 1) sheet.appendRow(HEADER);
    sheet.getRange(1, 1, 1, HEADER.length).setValues([HEADER]);

    const totalCols = HEADER.length + REMARKS_COLS;
    const last = sheet.getLastRow();
    const existingRange = last > 1
      ? sheet.getRange(2, 1, last - 1, totalCols).getValues()
      : [];
    const existing = {};
    existingRange.forEach(row => {
      const key = row[HEADER.length - 1];
      if (!key) return;
      existing[String(key)] = {
        data:    row.slice(0, HEADER.length),
        remarks: row.slice(HEADER.length, totalCols),
      };
    });

    if (action === "backfill") {
      Object.keys(existing).forEach(k => { existing[k].data = null; });
      incoming.forEach(r => {
        const k = String(r.unique_key || "");
        if (!k) return;
        if (!existing[k]) existing[k] = { remarks: new Array(REMARKS_COLS).fill("") };
        existing[k].data = rowToArr(r);
      });
      Object.keys(existing).forEach(k => { if (!existing[k].data) delete existing[k]; });
    } else {
      incoming.forEach(r => {
        const k = String(r.unique_key || "");
        if (!k) return;
        if (!existing[k]) existing[k] = { remarks: new Array(REMARKS_COLS).fill("") };
        existing[k].data = rowToArr(r);
      });
    }

    const sortedKeys = Object.keys(existing).sort((a, b) => {
      const dA = a.substring(0, 10), dB = b.substring(0, 10);
      if (dA !== dB) return dA < dB ? -1 : 1;
      const agA = String(existing[a].data[1] || "");
      const agB = String(existing[b].data[1] || "");
      return agA < agB ? -1 : (agA > agB ? 1 : 0);
    });

    const newBody = [];
    let prevDate = null;
    sortedKeys.forEach(k => {
      const d = k.substring(0, 10);
      if (prevDate !== null && d !== prevDate) {
        newBody.push(new Array(totalCols).fill(""));
      }
      const row = existing[k].data.concat(existing[k].remarks);
      while (row.length < totalCols) row.push("");
      newBody.push(row);
      prevDate = d;
    });

    if (last > 1) sheet.getRange(2, 1, last - 1, totalCols).clearContent();
    if (newBody.length) sheet.getRange(2, 1, newBody.length, totalCols).setValues(newBody);

    // ───────────────────────────────────────────────────────────────
    // Sheet2 — Per-Batch Log (NEW · Iter76)
    // ───────────────────────────────────────────────────────────────
    let batchTotal = 0;
    if (incomingBatch.length > 0) {
      let sheet2 = ss.getSheetByName("Sheet2");
      if (!sheet2) {
        sheet2 = ss.insertSheet("Sheet2");
      }
      if (sheet2.getLastRow() < 1) sheet2.appendRow(HEADER_BATCH);
      sheet2.getRange(1, 1, 1, HEADER_BATCH.length).setValues([HEADER_BATCH]);

      const totalColsB = HEADER_BATCH.length + REMARKS_COLS_BATCH;
      const lastB = sheet2.getLastRow();
      const existingRangeB = lastB > 1
        ? sheet2.getRange(2, 1, lastB - 1, totalColsB).getValues()
        : [];
      const existingB = {};
      existingRangeB.forEach(row => {
        const key = row[HEADER_BATCH.length - 1];
        if (!key) return;
        existingB[String(key)] = {
          data:    row.slice(0, HEADER_BATCH.length),
          remarks: row.slice(HEADER_BATCH.length, totalColsB),
        };
      });

      incomingBatch.forEach(r => {
        const k = String(r.unique_key || "");
        if (!k) return;
        if (r.deleted === true) {
          delete existingB[k];
          return;
        }
        if (!existingB[k]) existingB[k] = { remarks: new Array(REMARKS_COLS_BATCH).fill("") };
        existingB[k].data = batchRowToArr(r);
      });
      // Iter88: backfill = full refresh of Sheet2. Clear every existing
      // row's data (remarks preserved for keys that survive) then let
      // incoming batch_rows above repopulate. Stale rows whose group
      // no longer exists / whose message was deleted disappear on the
      // next refresh — matches Sheet1's backfill semantics exactly.
      if (action === "backfill") {
        // Two-pass: mark all existing as stale, then only keep those
        // present in the incoming payload (already merged above).
        Object.keys(existingB).forEach(k => {
          if (!incomingBatch.some(r => String(r.unique_key || "") === k)) {
            delete existingB[k];
          }
        });
      }

      // Sort by date (YYYY-MM-DD from key) ASC, then by time-of-day ASC.
      const sortedKeysB = Object.keys(existingB).sort((a, b) => {
        const dA = a.substring(0, 10), dB = b.substring(0, 10);
        if (dA !== dB) return dA < dB ? -1 : 1;
        // Time column is at index 1 (e.g. "09:15 AM IST"). Parse to 24h.
        const tA = parseISTTime(existingB[a].data[1]);
        const tB = parseISTTime(existingB[b].data[1]);
        return tA - tB;
      });

      const newBodyB = [];
      let prevDateB = null;
      sortedKeysB.forEach(k => {
        const d = k.substring(0, 10);
        if (prevDateB !== null && d !== prevDateB) {
          newBodyB.push(new Array(totalColsB).fill(""));
        }
        const row = existingB[k].data.concat(existingB[k].remarks);
        while (row.length < totalColsB) row.push("");
        newBodyB.push(row);
        prevDateB = d;
      });

      if (lastB > 1) sheet2.getRange(2, 1, lastB - 1, totalColsB).clearContent();
      if (newBodyB.length) sheet2.getRange(2, 1, newBodyB.length, totalColsB).setValues(newBodyB);
      batchTotal = newBodyB.length;
    }

    return out({
      ok: true,
      action: action,
      count: incoming.length,
      batch_count: incomingBatch.length,
      totalRows: newBody.length,
      totalBatchRows: batchTotal,
    });
  } catch (err) {
    return out({ ok: false, error: String(err) });
  } finally { lock.releaseLock(); }
}

function rowToArr(r) {
  return [
    r.date, r.agency, r.sent, r.done, r.pending, r.billable,
    r.uploaders || "", r.updated_at_iso, r.unique_key
  ];
}

function batchRowToArr(r) {
  return [
    r.date, r.time || "", r.agency, r.sent, r.done, r.pending, r.billable,
    r.uploaders || "", r.batch_id || "", r.updated_at_iso, r.unique_key
  ];
}

// Parse "09:15 AM IST" → minutes-since-midnight for stable sort. Falls back to 0.
function parseISTTime(t) {
  if (!t) return 0;
  const m = String(t).match(/(\\d{1,2}):(\\d{2})\\s*(AM|PM)/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ampm = m[3].toUpperCase();
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

function out(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}`;

// Apps Script for the COMPLAIN SHEET tab (separate webhook from the daily
// LPG report). Appends one row per complaint at the bottom of the tab named
// "COMPLAIN SHEET". Manual remarks added by admin are never overwritten.
const COMPLAINT_SCRIPT_SNIPPET = `/**
 * XYZ Chatting · COMPLAIN SHEET auto-append
 *
 * Behaviour:
 *   • Every complaint POSTed by the chat backend is appended as a new row
 *     at the bottom of the tab named "COMPLAIN SHEET".
 *   • Header is auto-created on first run.
 *   • Existing rows / manual remarks are NEVER touched.
 *
 * Setup:
 *   1. Open your complaint Google Sheet → Extensions → Apps Script
 *   2. Paste this code → Save → Deploy → Web app → Execute as: Me;
 *      Who has access: Anyone → Deploy → copy the Web app URL
 *   3. Paste that URL into Admin → Sheets → "Complaint Sheet Webhook URL"
 */
const COMPLAINT_TAB = "COMPLAIN SHEET";
const COMPLAINT_HEADER = [
  "Complaint Time","Kind","LPG Id","Agency","DAC","Booking Date",
  "Marked / Filed By","Status","Note","Message Id"
];

function doPost(e) {
  const lock = LockService.getScriptLock();
  lock.waitLock(20000);
  try {
    const body = JSON.parse(e.postData.contents);
    const rows = body.rows || [];
    const ss = SpreadsheetApp.getActiveSpreadsheet();
    let sh = ss.getSheetByName(COMPLAINT_TAB);
    if (!sh) sh = ss.insertSheet(COMPLAINT_TAB);
    if (sh.getLastRow() < 1) {
      sh.appendRow(COMPLAINT_HEADER);
    } else {
      // Keep header current (handles future column additions)
      sh.getRange(1, 1, 1, COMPLAINT_HEADER.length).setValues([COMPLAINT_HEADER]);
    }
    const matrix = rows.map(function (r) {
      return [
        r.complaint_at || "",
        r.kind || "",
        r.lpg_id || "",
        r.agency || "",
        r.dac_code || "",
        r.booking_date || "",
        r.marked_by || "",
        r.status || "",
        r.text || "",
        r.message_id || "",
      ];
    });
    if (matrix.length) {
      sh.getRange(sh.getLastRow() + 1, 1, matrix.length, COMPLAINT_HEADER.length)
        .setValues(matrix);
    }
    return out({ ok: true, appended: matrix.length });
  } catch (err) {
    return out({ ok: false, error: String(err) });
  } finally { lock.releaseLock(); }
}

function out(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}`;

// Complaint sheet card — admin pastes the Apps Script webhook URL for the
// "COMPLAIN SHEET" tab. Every new complaint → email + sheet row.
function ComplaintSheetCard() {
  const [url, setUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [status, setStatus] = useState(null); // {at, ok, error}
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      const u = data.complaint_sheet_webhook_url || "";
      setUrl(u);
      setSavedUrl(u);
      setStatus({
        at: data.last_complaint_sync_at,
        ok: data.last_complaint_sync_ok,
        error: data.last_complaint_sync_error,
      });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save"); setMsg(null);
    try {
      await api.put("/admin/settings", { complaint_sheet_webhook_url: url.trim() });
      setSavedUrl(url.trim());
      setMsg({ type: "ok", text: "Webhook saved. Naya complaint aate hi sheet me row add ho jayega." });
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test"); setMsg(null);
    try {
      await api.post("/admin/complaint-sheet/test");
      await load();
      setMsg({ type: "ok", text: "Test row appended to COMPLAIN SHEET. Sheet kholke verify karein (status='test')." });
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
    finally { setBusy(""); }
  };

  const copyScript = () => {
    navigator.clipboard.writeText(COMPLAINT_SCRIPT_SNIPPET).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm" data-testid="complaint-sheet-card">
      <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase mb-2 flex items-center gap-2">
        <Sheet className="w-3.5 h-3.5 text-[#C8553D]" /> Complaint Sheet (real-time append)
      </h3>
      <p className="text-xs text-[#6B6B61] mb-3 leading-relaxed">
        Har complaint email ke saath aapke <b>COMPLAIN SHEET</b> tab me automatic row add ho jayegi.
        Niche se Apps Script copy karein, sheet ke Apps Script editor me paste karein,
        Deploy ke baad jo URL mile woh yahan paste karein.
      </p>

      <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
        Apps Script Web App URL (COMPLAIN SHEET)
      </label>
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://script.google.com/macros/s/..../exec"
        data-testid="complaint-sheet-url-input"
        className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-[#385C4B]"
      />

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={save}
          disabled={busy === "save" || url.trim() === savedUrl}
          data-testid="complaint-sheet-save-button"
          className="flex items-center gap-2 bg-[#385C4B] text-[#F5F5F0] hover:bg-[#2D4A3C] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
        >
          {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Save Webhook
        </button>
        <button
          onClick={test}
          disabled={busy === "test" || !savedUrl}
          data-testid="complaint-sheet-test-button"
          className="flex items-center gap-2 bg-white border border-[#DCDCD3] text-[#1C1C19] hover:border-[#385C4B] hover:text-[#385C4B] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
        >
          {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          Send Test Row
        </button>
        <button
          onClick={() => setShowScript((s) => !s)}
          data-testid="complaint-sheet-toggle-script"
          className="flex items-center gap-2 bg-white border border-[#DCDCD3] text-[#6B6B61] hover:text-[#1C1C19] px-3 py-2 rounded-md text-sm transition-colors"
        >
          {showScript ? "Hide" : "Show"} Apps Script
        </button>
      </div>

      {status && status.at && (
        <div className="mt-3 text-xs text-[#6B6B61]">
          Last sync: <b>{status.at}</b>{" "}
          {status.ok ? <span className="text-[#385C4B]">✓ ok</span>
            : <span className="text-[#8A6046]">✗ {status.error || "failed"}</span>}
        </div>
      )}

      {msg && (
        <div
          data-testid="complaint-sheet-status-message"
          className={`mt-3 text-sm px-3 py-2 rounded-md flex items-start gap-2 ${
            msg.type === "ok"
              ? "bg-[#385C4B]/10 text-[#385C4B] border border-[#385C4B]/30"
              : "bg-[#8A6046]/10 text-[#8A6046] border border-[#8A6046]/30"
          }`}
        >
          {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {showScript && (
        <div className="mt-3 relative">
          <button
            onClick={copyScript}
            data-testid="complaint-sheet-copy-script"
            className="absolute top-2 right-2 z-10 bg-white border border-[#DCDCD3] hover:border-[#385C4B] px-2 py-1 rounded text-[10px] font-mono tracking-[0.15em] uppercase text-[#385C4B]"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <pre className="bg-[#1C1C19] text-[#F5F5F0] text-[11px] font-mono p-3 rounded overflow-auto max-h-[300px] leading-snug">
            {COMPLAINT_SCRIPT_SNIPPET}
          </pre>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sheet Inbox — reverse flow (Google Sheet → Chat)
// Admin pastes the token shown here into an Apps Script trigger; whenever a
// new row is added to the complaint sheet, it auto-posts to the matching
// agency's chat as the "Sheet Bot" user (using the same LPG regex parser).
// ============================================================================
const SHEET_INBOX_SCRIPT_TEMPLATE = (token, backendUrl) => `/**
 * XYZ Chatting · Sheet -> Chat auto-post
 *
 * Behaviour: When a row is added/edited in this spreadsheet, the row is
 * POSTed to the chat backend. Backend matches column B = agency name and
 * posts the row in the chat using the existing LPG parser (Sheet Bot user).
 *
 * Setup:
 *   1. Open this Google Sheet -> Extensions -> Apps Script
 *   2. Paste this code -> Save
 *   3. Top-left clock icon -> Add Trigger -> Function: onEditTrigger,
 *      Event source: From spreadsheet, Event type: On change -> Save
 *   4. Authorize when prompted.
 *
 * NOTE: If you rotate the token in the admin panel, paste the new one here.
 */
const BACKEND_URL = ${JSON.stringify(backendUrl)};
const SHEET_TOKEN = ${JSON.stringify(token || "PASTE_TOKEN_HERE")};

function onEditTrigger(e) {
  try {
    const range = e.range;
    if (!range) return;
    const sheet = range.getSheet();
    if (range.getRow() < 2) return;
    const lastCol = Math.max(sheet.getLastColumn(), 10);
    const rowVals = sheet.getRange(range.getRow(), 1, 1, lastCol).getValues()[0];
    if (!rowVals.some(function (v) { return v !== "" && v !== null; })) return;
    UrlFetchApp.fetch(BACKEND_URL + "/api/sheet-inbox/dac-rows", {
      method: "post",
      contentType: "application/json",
      headers: { "X-Sheet-Token": SHEET_TOKEN },
      muteHttpExceptions: true,
      payload: JSON.stringify({ rows: [{ cells: rowVals }] }),
    });
  } catch (err) {
    console.error("Sheet-to-chat failed:", err);
  }
}`;

// ============================================================================
// Delivered Log Webhook — agency Tick → aggregator Google Sheet append
// ============================================================================
const DELIVERED_LOG_SCRIPT_SNIPPET = `/**
 * XYZ Chatting · DELIVERED LOG aggregator
 *
 * Every time an agency clicks the "Tick" / Untick button on the Real Time
 * view, the backend POSTs a row here. One central sheet logs delivery
 * confirmations from ALL agencies in mix order — with full audit trail
 * (DELIVERED + UNDONE events).
 *
 * Behaviour:
 *  - Duplicate skip: same {action + agency + LPG + DAC + delivery_date}
 *    already logged earlier? Skipped automatically.
 *  - Date separator: when delivery_date changes vs the previous row,
 *    2 blank rows are inserted as a visual day-separator.
 *  - 16-digit LPG IDs stored as TEXT (no scientific notation).
 *
 * SETUP (VERY IMPORTANT):
 *  1. Aggregator sheet → Extensions → Apps Script
 *  2. Select default code (Ctrl+A) → Delete → paste THIS code → Save (Ctrl+S)
 *  3. Deploy → New deployment → Web app:
 *       Execute as: Me
 *       Who has access: ANYONE  (NOT "Anyone with Google account")
 *  4. Copy the /exec URL → paste in Admin → Sheets → Delivered Log Webhook
 *  5. Hit "Test Webhook" → green ✓ confirms it works.
 */
const LOG_TAB = "DELIVERED LOG";
const HEADER = [
  "Logged At",
  "Action",
  "Agency",
  "LPG Consumer ID",
  "DAC Code",
  "Mobile",
  "Delivery Date",
  "Marked By",
  "Marked At (IST)",
];

function doPost(e) {
  const lock = LockService.getScriptLock();
  lock.waitLock(15000);
  try {
    const body = JSON.parse(e.postData.contents);
    const rows = body.rows || [];
    const ss = SpreadsheetApp.getActiveSpreadsheet();
    let sh = ss.getSheetByName(LOG_TAB);
    if (!sh) {
      sh = ss.insertSheet(LOG_TAB, 0);
      sh.appendRow(HEADER);
      sh.setFrozenRows(1);
      sh.getRange(1, 1, 1, HEADER.length).setFontWeight("bold");
    } else if (sh.getLastRow() < 1) {
      sh.appendRow(HEADER);
    } else {
      sh.getRange(1, 1, 1, HEADER.length).setValues([HEADER]);
    }

    const tz = ss.getSpreadsheetTimeZone() || "Asia/Kolkata";

    let existing = [];
    const lastRow = sh.getLastRow();
    if (lastRow >= 2) {
      existing = sh.getRange(2, 1, lastRow - 1, HEADER.length).getValues();
    }

    const keyOf = function (action, agency, lpg, dac, date) {
      return [
        String(action || "").trim().toUpperCase(),
        String(agency || "").trim().toUpperCase(),
        String(lpg || "").trim(),
        String(dac || "").trim(),
        String(date || "").trim(),
      ].join("|");
    };

    const seen = {};
    for (let i = 0; i < existing.length; i++) {
      const r = existing[i];
      if (!r[1] && !r[3]) continue;
      seen[keyOf(r[1], r[2], r[3], r[4], r[6])] = true;
    }

    let lastDate = "";
    for (let i = existing.length - 1; i >= 0; i--) {
      const d = String(existing[i][6] || "").trim();
      if (d) { lastDate = d; break; }
    }

    let appended = 0;
    let deduped = 0;
    let nextRow = sh.getLastRow() + 1;

    for (let idx = 0; idx < rows.length; idx++) {
      const r = rows[idx];
      const action = String(r.action || "");
      const agency = String(r.agency || "");
      const lpg = String(r.lpg_id || "");
      const dac = String(r.dac || "");
      const mobile = String(r.mobile || "");
      const deliveryDate = String(r.delivery_date || "");
      const markedBy = String(r.marked_by || "");
      const markedAtIst = String(r.marked_at_ist || "");

      const key = keyOf(action, agency, lpg, dac, deliveryDate);
      if (seen[key]) {
        deduped++;
        continue;
      }

      if (lastDate && deliveryDate && lastDate !== deliveryDate) {
        nextRow += 2;
      }

      const loggedAt = Utilities.formatDate(new Date(), tz, "dd-MM-yyyy hh:mm a");
      const range = sh.getRange(nextRow, 1, 1, HEADER.length);
      range.setNumberFormat("@");
      range.setValues([[
        loggedAt,
        action,
        agency,
        lpg,
        dac,
        mobile,
        deliveryDate,
        markedBy,
        markedAtIst,
      ]]);

      seen[key] = true;
      if (deliveryDate) lastDate = deliveryDate;
      nextRow++;
      appended++;
    }

    return out({ ok: true, appended: appended, deduped: deduped });
  } catch (err) {
    return out({ ok: false, error: String(err) });
  } finally {
    lock.releaseLock();
  }
}

function doGet() {
  return out({ ok: true, service: "delivered-log", tab: LOG_TAB });
}

function out(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}`;

function DeliveredWebhookCard() {
  const [url, setUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);

  const copyScript = () => {
    navigator.clipboard.writeText(DELIVERED_LOG_SCRIPT_SNIPPET).then(() => {
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    }).catch(() => {});
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      const u = data.delivered_webhook_url || "";
      setUrl(u);
      setSavedUrl(u);
      if (data.last_delivered_sync_at) {
        setLastSync({
          at: data.last_delivered_sync_at,
          ok: data.last_delivered_sync_ok,
          status: data.last_delivered_sync_status,
          error: data.last_delivered_sync_error,
        });
      }
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put("/admin/settings", { delivered_webhook_url: url.trim() });
      setSavedUrl(url.trim());
      setMsg({
        type: "ok",
        text: url.trim()
          ? "Webhook saved. Ab 'Test Webhook' button dabake verify karein."
          : "Webhook clear ho gaya. Delivered log sync abhi OFF hai.",
      });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/delivered-webhook/test");
      setTestResult(data);
      // Refresh last sync from DB
      await load();
    } catch (e) {
      const errText = formatApiError(e);
      setTestResult({
        ok: false,
        status: 0,
        body_preview: "",
        diagnosis: errText,
      });
      await load();
    } finally {
      setTesting(false);
    }
  };

  const dirty = url.trim() !== savedUrl.trim();

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5" data-testid="delivered-webhook-card">
      <div className="flex items-baseline gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
        </span>
        <h3 className="text-lg font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Delivered Log Sheet · Webhook
        </h3>
      </div>
      <p className="text-xs text-[#6B6B61] mt-1.5 leading-relaxed">
        Aggregator Google Sheet ka <b>Apps Script Web App URL</b> yahan
        paste karein. Jab koi agency Real Time view me <b>Tick</b> dabaye,
        backend us URL par row POST karega — ek hi sheet me saari agencies
        ka delivery log mix order me aata rahega. Untick hone par bhi row
        ka <i>action=UNDONE</i> aayega (audit trail).
      </p>
      <p className="text-[11px] text-[#8A6046] mt-2 leading-relaxed">
        <b>Setup (ZAROORI):</b> aggregator sheet → Extensions → Apps Script → ye
        code paste (DELIVERED LOG script) → Deploy → New deployment → Web
        app → Execute as: <b>Me</b>, Who has access: <b>ANYONE</b> (NOT "Anyone
        with Google account") → copy URL → yahan paste → Save → <b>Test
        Webhook</b> dabake verify.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://script.google.com/macros/s/.../exec"
          data-testid="delivered-webhook-input"
          className="px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-[#385C4B]"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={save}
            disabled={busy || !dirty}
            data-testid="delivered-webhook-save"
            className="px-4 py-1.5 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save Webhook
          </button>
          <button
            onClick={runTest}
            disabled={testing || !savedUrl || dirty}
            data-testid="delivered-webhook-test"
            className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
            title={dirty ? "Save first, then test" : "Send a dummy DELIVERED row to verify the webhook"}
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radio className="w-3.5 h-3.5" />}
            Test Webhook
          </button>
          <button
            onClick={() => setShowScript((s) => !s)}
            data-testid="delivered-webhook-toggle-script"
            className="px-3 py-1.5 bg-white border border-[#DCDCD3] text-[#6B6B61] text-sm rounded hover:text-[#1C1C19] hover:border-[#385C4B] flex items-center gap-2"
          >
            {showScript ? "Hide" : "Show"} Apps Script
          </button>
          {savedUrl && !dirty && (
            <span className="text-[11px] text-emerald-700 font-mono">
              ✓ Saved
            </span>
          )}
        </div>
      </div>

      {testResult && (
        <div
          className={`mt-3 px-3 py-3 text-xs rounded border ${
            testResult.ok
              ? "bg-emerald-500/10 text-emerald-800 border-emerald-500/30"
              : "bg-red-500/10 text-red-800 border-red-500/30"
          }`}
          data-testid="delivered-webhook-test-result"
        >
          <div className="font-semibold flex items-center gap-2 mb-1.5">
            {testResult.ok ? "✅ Webhook working" : "❌ Webhook failed"}
            <span className="font-mono text-[10px] opacity-70">
              HTTP {testResult.status}
            </span>
            {testResult.appended !== null && testResult.appended !== undefined && (
              <span className="font-mono text-[10px] opacity-70">
                · appended={testResult.appended}
              </span>
            )}
          </div>
          {testResult.diagnosis && (
            <p className="leading-relaxed mb-2">
              <b>Fix:</b> {testResult.diagnosis}
            </p>
          )}
          {testResult.body_preview && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[11px] opacity-80">
                Response body preview
              </summary>
              <pre className="mt-1 p-2 bg-black/5 rounded text-[10px] font-mono whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                {testResult.body_preview}
              </pre>
            </details>
          )}
          {testResult.ok && (
            <p className="text-[11px] opacity-80 mt-1">
              Check aggregator sheet → "DELIVERED LOG" tab — ek row mil jayegi
              agency = <b>__TEST_AGENCY__</b>. Use delete kar dein.
            </p>
          )}
        </div>
      )}

      {!testResult && lastSync && (
        <div
          className={`mt-3 px-3 py-2 text-[11px] rounded border ${
            lastSync.ok
              ? "bg-emerald-500/5 text-emerald-700 border-emerald-500/20"
              : "bg-red-500/5 text-red-700 border-red-500/20"
          }`}
          data-testid="delivered-webhook-last-sync"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">
              Last sync: {lastSync.ok ? "✓ OK" : "✗ FAIL"}
            </span>
            <span className="opacity-70 font-mono">
              HTTP {lastSync.status ?? "—"}
            </span>
            <span className="opacity-70">
              · {new Date(lastSync.at).toLocaleString("en-IN")}
            </span>
          </div>
          {!lastSync.ok && lastSync.error && (
            <p className="mt-1 opacity-90 leading-relaxed">{lastSync.error}</p>
          )}
        </div>
      )}

      {msg && (
        <div
          className={`mt-3 px-3 py-2 text-xs rounded ${
            msg.type === "ok"
              ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
              : "bg-red-500/10 text-red-700 border border-red-500/30"
          }`}
          data-testid="delivered-webhook-msg"
        >
          {msg.text}
        </div>
      )}

      {showScript && (
        <div className="mt-4 relative" data-testid="delivered-webhook-script-block">
          <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#A7A79D] bg-black/40 px-2 py-1 rounded">
              Code.gs
            </span>
            <button
              onClick={copyScript}
              data-testid="delivered-webhook-copy-script"
              className="bg-white border border-[#DCDCD3] hover:border-[#385C4B] px-3 py-1 rounded text-[10px] font-mono tracking-[0.15em] uppercase text-[#385C4B]"
            >
              {scriptCopied ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <pre className="bg-[#1C1C19] text-[#F5F5F0] text-[11px] font-mono p-3 pt-10 rounded overflow-auto max-h-[360px] leading-snug whitespace-pre">
            {DELIVERED_LOG_SCRIPT_SNIPPET}
          </pre>
          <p className="text-[10px] text-[#6B6B61] mt-1.5 leading-relaxed">
            <b>How to use:</b> ☝️ Copy dabake aggregator sheet ke Apps Script
            editor me <b>Ctrl+A → Delete → Paste</b> karein → <b>Save</b> →
            Deploy → Manage deployments → pencil → <b>New version</b> → Deploy.
          </p>
        </div>
      )}
    </div>
  );
}



function SheetInboxCard() {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [copied, setCopied] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const { data } = await api.get("/admin/sheet-inbox/token");
      setToken(data.token);
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
  };
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm("Rotate sheet token? Purana token aage kaam nahi karega.")) return;
    setBusy("rotate"); setMsg(null);
    try {
      const { data } = await api.post("/admin/sheet-inbox/rotate-token");
      setToken(data.token);
      setMsg({ type: "ok", text: "Token rotated. Apps Script me new token paste karein." });
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
    finally { setBusy(""); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const scriptCode = SHEET_INBOX_SCRIPT_TEMPLATE(token, backendUrl);

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm" data-testid="sheet-inbox-card">
      <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase mb-2 flex items-center gap-2">
        <Sheet className="w-3.5 h-3.5 text-[#385C4B]" /> Sheet &rarr; Chat (reverse sync)
      </h3>
      <p className="text-xs text-[#6B6B61] mb-3 leading-relaxed">
        Apke complaint sheet me <b>new DAC row</b> jodne par ye automatic
        agency ke chat group me <b>Sheet Bot</b> ke naam se post ho jayegi.
        Niche se Apps Script copy karein, sheet me paste karein, aur
        <b> onEditTrigger</b> ka trigger configure karein.
      </p>

      <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
        Sheet Token (X-Sheet-Token header)
      </label>
      <div className="flex items-center gap-2 mt-1">
        <input
          readOnly
          value={token}
          data-testid="sheet-inbox-token"
          className="flex-1 px-3 py-2 border border-[#DCDCD3] rounded text-xs font-mono bg-[#F5F5F0]"
        />
        <button
          onClick={() => copy(token, "token")}
          data-testid="sheet-inbox-copy-token"
          className="px-3 py-2 text-xs font-mono tracking-[0.15em] uppercase text-[#385C4B] border border-[#DCDCD3] hover:border-[#385C4B] rounded"
        >
          {copied === "token" ? "Copied" : "Copy"}
        </button>
        <button
          onClick={rotate}
          disabled={busy === "rotate"}
          data-testid="sheet-inbox-rotate-button"
          className="px-3 py-2 text-xs font-mono tracking-[0.15em] uppercase text-[#8A6046] border border-[#DCDCD3] hover:border-[#8A6046] disabled:opacity-50 rounded"
        >
          {busy === "rotate" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Rotate"}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => setShowScript((s) => !s)}
          data-testid="sheet-inbox-toggle-script"
          className="flex items-center gap-2 bg-[#385C4B] text-[#F5F5F0] hover:bg-[#2D4A3C] px-4 py-2 rounded-md text-sm transition-colors"
        >
          {showScript ? "Hide" : "Show"} Apps Script (with token embedded)
        </button>
      </div>

      {msg && (
        <div
          data-testid="sheet-inbox-status-message"
          className={`mt-3 text-sm px-3 py-2 rounded-md flex items-start gap-2 ${
            msg.type === "ok"
              ? "bg-[#385C4B]/10 text-[#385C4B] border border-[#385C4B]/30"
              : "bg-[#8A6046]/10 text-[#8A6046] border border-[#8A6046]/30"
          }`}
        >
          {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {showScript && (
        <div className="mt-3 relative">
          <button
            onClick={() => copy(scriptCode, "script")}
            data-testid="sheet-inbox-copy-script"
            className="absolute top-2 right-2 z-10 bg-white border border-[#DCDCD3] hover:border-[#385C4B] px-2 py-1 rounded text-[10px] font-mono tracking-[0.15em] uppercase text-[#385C4B]"
          >
            {copied === "script" ? "Copied" : "Copy"}
          </button>
          <pre className="bg-[#1C1C19] text-[#F5F5F0] text-[11px] font-mono p-3 rounded overflow-auto max-h-[400px] leading-snug">
            {scriptCode}
          </pre>
          <p className="mt-2 text-[11px] text-[#8A6046] leading-relaxed">
            <b>Trigger setup:</b> Apps Script editor me top-left clock icon
            (Triggers) &rarr; Add Trigger &rarr; Function: <b>onEditTrigger</b>,
            Event source: <b>From spreadsheet</b>, Event type: <b>On change</b>
            &rarr; Save. Pehli baar permission allow karein.
          </p>
        </div>
      )}
    </div>
  );
}



// Email notification card — Gmail SMTP credentials live in backend/.env.
// This card just lets admin verify the wiring + manually trigger a digest.
function EmailNotifyCard() {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const send = async (kind) => {
    setBusy(kind);
    setMsg(null);
    try {
      const url = kind === "test" ? "/admin/email/test" : "/admin/email/digest-now";
      const { data } = await api.post(url);
      const txt = kind === "test"
        ? `Test email sent to ${(data.sent_to || []).join(", ")}. Inbox check karein.`
        : "Daily digest dispatched (open complaints summary).";
      setMsg({ type: "ok", text: txt });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm" data-testid="email-notify-card">
      <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase mb-2 flex items-center gap-2">
        <Inbox className="w-3.5 h-3.5 text-[#C8553D]" /> Email Notifications
      </h3>
      <p className="text-xs text-[#6B6B61] mb-3 leading-relaxed">
        Har complaint par <b>Ashish</b> ko Gmail par instant email jaata hai.
        Agar din me resolve nahi hua to <b>agle din 9 AM IST</b> ko consolidated
        digest bhi jayega. Sender/recipient/SMTP credentials backend ke
        environment me secure rakhe hain.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => send("test")}
          disabled={busy === "test"}
          data-testid="email-test-button"
          className="flex items-center gap-2 bg-[#385C4B] text-[#F5F5F0] hover:bg-[#2D4A3C] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
        >
          {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          Send Test Email
        </button>
        <button
          onClick={() => send("digest")}
          disabled={busy === "digest"}
          data-testid="email-digest-button"
          className="flex items-center gap-2 bg-white border border-[#DCDCD3] text-[#1C1C19] hover:border-[#385C4B] hover:text-[#385C4B] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
        >
          {busy === "digest" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Send Digest Now
        </button>
      </div>
      {msg && (
        <div
          data-testid="email-status-message"
          className={`mt-3 text-sm px-3 py-2 rounded-md flex items-start gap-2 ${
            msg.type === "ok"
              ? "bg-[#385C4B]/10 text-[#385C4B] border border-[#385C4B]/30"
              : "bg-[#8A6046]/10 text-[#8A6046] border border-[#8A6046]/30"
          }`}
        >
          {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}
    </div>
  );
}

const REALTIME_AGGREGATOR_SCRIPT_SNIPPET = `/**
 * XYZ Chatting · REALTIME AGGREGATOR — DAILY SNAPSHOT
 *
 * Aggregator Google Sheet me roz subah 6:30 AM IST ko backend automatically
 * previous day ka data POST karega. Apps Script har agency ke liye ek
 * separate tab banata hai aur waha rows append karta hai.
 *
 * MASTER SHEET tab: ek hi consolidated tab jisme HAR agency ka HAR row
 * aata hai — delivered ho ya na ho, dono. Staff total milan
 * (reconciliation) ke liye single source of truth.
 *
 * Payload shape (incoming POST):
 *   { snapshot_date: "DD.MM.YYYY",
 *     agencies: [
 *       { agency: "BHOOSHAN INDANE",
 *         rows: [{date, agency, lpg_id, mobile, dac, delivered, marked_by}, ...] },
 *       ...
 *     ],
 *     master_rows: [
 *       { agency, date, lpg_id, mobile, dac, delivered, marked_by }, ...
 *     ]
 *   }
 *
 * Setup (1 baar):
 *  1. Empty Google Sheet open karein → Extensions → Apps Script
 *  2. Default code Ctrl+A → Delete → ye full code paste → Save (Ctrl+S)
 *  3. Deploy → New deployment → Web app:
 *       Execute as: Me
 *       Who has access: ANYONE (NOT "Anyone with Google account")
 *  4. /exec URL copy → Admin Console → Sheets tab →
 *     "Real-time Aggregator · Daily Snapshot" card me paste → Save
 *  5. "Test Webhook" dabake green ✓ verify
 *  6. Done! Daily 6:30 AM IST snapshot apne aap aata rahega
 */
const HEADER = [
  "Snapshot Date",
  "Delivery Date",
  "Agency",
  "LPG Consumer ID",
  "Mobile",
  "DAC Code",
  "Delivered",
  "Marked By",
];

// MASTER SHEET — ALL rows from ALL agencies (delivered + non-delivered).
const MASTER_TAB = "MASTER SHEET";
const MASTER_HEADER = [
  "Snapshot Date",
  "Agency",
  "Delivery Date",
  "LPG Consumer ID",
  "Mobile",
  "DAC Code",
  "Delivered",
  "Marked By",
];

function doPost(e) {
  const lock = LockService.getScriptLock();
  lock.waitLock(20000);
  try {
    const body = JSON.parse(e.postData.contents);
    const snapDate = String(body.snapshot_date || "");
    const agencies = body.agencies || [];
    // Backward compat: accept master_rows (new) OR master_pending (old).
    const masterRows = body.master_rows || body.master_pending || [];
    const ss = SpreadsheetApp.getActiveSpreadsheet();
    const tz = ss.getSpreadsheetTimeZone() || "Asia/Kolkata";
    const loggedAt = Utilities.formatDate(new Date(), tz, "dd-MM-yyyy hh:mm a");
    let totalAppended = 0;
    let tabsTouched = 0;

    for (const a of agencies) {
      const tabName = String(a.agency || "UNNAMED").substring(0, 90).trim();
      if (!tabName) continue;
      const rows = a.rows || [];
      if (!rows.length) continue;
      let sh = ss.getSheetByName(tabName);
      if (!sh) {
        sh = ss.insertSheet(tabName);
        sh.appendRow(HEADER);
        sh.setFrozenRows(1);
        sh.getRange(1, 1, 1, HEADER.length).setFontWeight("bold").setBackground("#d9ead3");
      } else if (sh.getLastRow() < 1) {
        sh.appendRow(HEADER);
      }
      // De-dup: skip rows whose (snapshot_date + lpg_id) already exists
      const lastRow = sh.getLastRow();
      const existing = lastRow >= 2
        ? sh.getRange(2, 1, lastRow - 1, HEADER.length).getValues()
        : [];
      const seen = {};
      for (const r of existing) {
        const key = String(r[0] || "") + "|" + String(r[3] || "");
        seen[key] = true;
      }
      const toAppend = [];
      for (const r of rows) {
        const lpg = String(r.lpg_id || "");
        const key = snapDate + "|" + lpg;
        if (seen[key]) continue;
        toAppend.push([
          snapDate,
          String(r.date || ""),
          String(r.agency || tabName),
          lpg,
          String(r.mobile || ""),
          String(r.dac || ""),
          String(r.delivered || ""),
          String(r.marked_by || ""),
        ]);
      }
      if (!toAppend.length) continue;
      const startRow = sh.getLastRow() + 1;
      const rng = sh.getRange(startRow, 1, toAppend.length, HEADER.length);
      rng.setNumberFormat("@");           // text — preserves 16-digit IDs
      rng.setValues(toAppend);
      totalAppended += toAppend.length;
      tabsTouched++;
    }

    // -----------------------------------------------------------------
    // MASTER SHEET: ALL rows from ALL agencies (delivered + non-delivered).
    // De-dup by (snapshot_date + agency + lpg_id) — re-runs of the same
    // day don't bloat the tab.
    // -----------------------------------------------------------------
    let masterAppended = 0;
    if (masterRows.length) {
      let msh = ss.getSheetByName(MASTER_TAB);
      if (!msh) {
        msh = ss.insertSheet(MASTER_TAB, 0);  // pin to first position
        msh.appendRow(MASTER_HEADER);
        msh.setFrozenRows(1);
        msh.getRange(1, 1, 1, MASTER_HEADER.length)
           .setFontWeight("bold").setBackground("#fce5cd");
      } else if (msh.getLastRow() < 1) {
        msh.appendRow(MASTER_HEADER);
      }
      const mLastRow = msh.getLastRow();
      const mExisting = mLastRow >= 2
        ? msh.getRange(2, 1, mLastRow - 1, MASTER_HEADER.length).getValues()
        : [];
      const mSeen = {};
      for (const r of mExisting) {
        // master schema: [snapshot_date, agency, date, lpg, mobile, dac, delivered, marked_by]
        const key = String(r[0] || "") + "|" + String(r[1] || "") + "|" + String(r[3] || "");
        mSeen[key] = true;
      }
      const mToAppend = [];
      for (const r of masterRows) {
        const lpg = String(r.lpg_id || "");
        const ag = String(r.agency || "");
        const key = snapDate + "|" + ag + "|" + lpg;
        if (!lpg || mSeen[key]) continue;
        mSeen[key] = true;
        mToAppend.push([
          snapDate,
          ag,
          String(r.date || ""),
          lpg,
          String(r.mobile || ""),
          String(r.dac || ""),
          String(r.delivered || ""),
          String(r.marked_by || ""),
        ]);
      }
      if (mToAppend.length) {
        const mStart = msh.getLastRow() + 1;
        const mRng = msh.getRange(mStart, 1, mToAppend.length, MASTER_HEADER.length);
        mRng.setNumberFormat("@");
        mRng.setValues(mToAppend);
        masterAppended = mToAppend.length;
      }
    }

    return out({
      ok: true,
      snapshot_date: snapDate,
      tabs_touched: tabsTouched,
      rows_appended: totalAppended,
      master_rows_appended: masterAppended,
      logged_at: loggedAt,
    });
  } catch (err) {
    return out({ ok: false, error: String(err) });
  } finally {
    lock.releaseLock();
  }
}

function doGet() {
  return out({ ok: true, service: "realtime-aggregator" });
}

function out(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}`;


function RealtimeAggregatorCard() {
  const [url, setUrl] = useState("");
  const [savedUrl, setSavedUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });

  const copyScript = () => {
    navigator.clipboard.writeText(REALTIME_AGGREGATOR_SCRIPT_SNIPPET).then(() => {
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    }).catch(() => {});
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setUrl(data.realtime_aggregator_webhook_url || "");
      setSavedUrl(data.realtime_aggregator_webhook_url || "");
      if (data.last_realtime_snapshot_at) {
        setLastSync({
          at: data.last_realtime_snapshot_at,
          ok: data.last_realtime_snapshot_ok,
          summary: data.last_realtime_snapshot_summary,
        });
      }
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save"); setMsg(null);
    try {
      await api.put("/admin/settings", { realtime_aggregator_webhook_url: url.trim() });
      setSavedUrl(url.trim());
      setMsg({ type: "ok", text: "Webhook saved. Now hit Test." });
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
    finally { setBusy(""); }
  };

  const runTest = async () => {
    setBusy("test"); setTestResult(null); setMsg(null);
    try {
      const { data } = await api.post("/admin/realtime-aggregator/test");
      setTestResult(data);
      await load();
    } catch (e) {
      setTestResult({ ok: false, status: 0, diagnosis: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const runNow = async () => {
    setBusy("run"); setMsg(null);
    try {
      const { data } = await api.post(`/admin/realtime-aggregator/run-now?target_date=${date}`);
      setMsg({
        type: data.ok ? "ok" : "error",
        text: data.ok
          ? `Snapshot pushed · ${data.summary || (data.agencies + " agencies, " + data.rows + " rows")}`
          : `Failed: ${data.error || data.body_preview || "see backend logs"}`,
      });
      await load();
    } catch (e) { setMsg({ type: "error", text: formatApiError(e) }); }
    finally { setBusy(""); }
  };

  const dirty = url.trim() !== savedUrl.trim();

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5" data-testid="realtime-aggregator-card">
      <div className="flex items-baseline gap-2">
        <span className="text-blue-700">📊</span>
        <h3 className="text-lg font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Real-time Aggregator · Daily Snapshot
        </h3>
      </div>
      <p className="text-xs text-[#6B6B61] mt-1.5 leading-relaxed">
        Roz subah <b>6:30 AM IST</b> backend automatically <b>previous day</b> ka
        Real-Time data har agency se fetch karega aur aapke central Google
        Sheet me push karega — har agency ka apna alag tab. Columns:
        Snapshot Date, Delivery Date, Agency, LPG ID, Mobile, DAC,
        Delivered (✓), Marked By. Duplicate rows skip honge automatically.
        <br />
        <span className="text-amber-700">
          <b>MASTER SHEET tab:</b> ek hi tab me sab agencies ke <b>SAARE</b>{" "}
          rows (delivered + non-delivered DONO) mix order me — staff total
          milan (reconciliation) ke liye single source of truth.
        </span>
      </p>
      <p className="text-[11px] text-[#8A6046] mt-2 leading-relaxed">
        <b>Setup:</b> empty Google Sheet → Apps Script editor → niche{" "}
        <b>Show Apps Script</b> dabake code copy karein → paste → Deploy →
        Web app → <b>ANYONE</b> access → /exec URL paste here → Save → Test.
      </p>
      <div className="mt-3 flex flex-col gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://script.google.com/macros/s/.../exec"
          data-testid="realtime-aggregator-input"
          className="px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={save} disabled={busy || !dirty} data-testid="realtime-aggregator-save"
            className="px-4 py-1.5 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Save
          </button>
          <button onClick={runTest} disabled={!!busy || !savedUrl || dirty} data-testid="realtime-aggregator-test"
            className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2">
            {busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🧪"}
            Test Webhook
          </button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="realtime-aggregator-date"
            className="px-2 py-1 border border-[#DCDCD3] rounded text-xs" />
          <button onClick={runNow} disabled={!!busy || !savedUrl || dirty} data-testid="realtime-aggregator-run-now"
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            title="Manually trigger snapshot for selected date (default: yesterday)">
            {busy === "run" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🚀"}
            Run Now
          </button>
          <button
            onClick={() => setShowScript((v) => !v)}
            data-testid="realtime-aggregator-toggle-script"
            className="px-4 py-1.5 bg-white border border-[#DCDCD3] text-[#385C4B] text-sm rounded hover:border-[#385C4B] flex items-center gap-2"
          >
            {showScript ? "Hide Apps Script" : "Show Apps Script"}
          </button>
        </div>
      </div>
      {testResult && (
        <div className={`mt-3 px-3 py-3 text-xs rounded border ${
          testResult.ok ? "bg-emerald-500/10 text-emerald-800 border-emerald-500/30"
                        : "bg-red-500/10 text-red-800 border-red-500/30"
        }`} data-testid="realtime-aggregator-test-result">
          <div className="font-semibold mb-1.5">
            {testResult.ok ? "✅ Test Passed" : "❌ Test Failed"} · HTTP {testResult.status}
          </div>
          {testResult.diagnosis && <p className="mb-1">{testResult.diagnosis}</p>}
          {testResult.body_preview && (
            <details><summary className="cursor-pointer text-[11px]">Response preview</summary>
              <pre className="mt-1 p-2 bg-black/5 rounded text-[10px] whitespace-pre-wrap break-all max-h-24 overflow-y-auto">{testResult.body_preview}</pre>
            </details>
          )}
        </div>
      )}
      {lastSync && (
        <div className={`mt-3 px-3 py-2 text-[11px] rounded border ${
          lastSync.ok ? "bg-emerald-500/5 text-emerald-700 border-emerald-500/20"
                      : "bg-red-500/5 text-red-700 border-red-500/20"
        }`} data-testid="realtime-aggregator-last-sync">
          <b>Last snapshot:</b> {lastSync.ok ? "✓" : "✗"} ·{" "}
          {new Date(lastSync.at).toLocaleString("en-IN")} · {lastSync.summary}
        </div>
      )}
      {msg && (
        <div className={`mt-3 px-3 py-2 text-xs rounded ${
          msg.type === "ok" ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
                            : "bg-red-500/10 text-red-700 border border-red-500/30"
        }`} data-testid="realtime-aggregator-msg">{msg.text}</div>
      )}
      {showScript && (
        <div className="mt-4 relative" data-testid="realtime-aggregator-script-block">
          <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#A7A79D] bg-black/40 px-2 py-1 rounded">
              Code.gs
            </span>
            <button
              onClick={copyScript}
              data-testid="realtime-aggregator-copy-script"
              className="bg-white border border-[#DCDCD3] hover:border-[#385C4B] px-3 py-1 rounded text-[10px] font-mono tracking-[0.15em] uppercase text-[#385C4B]"
            >
              {scriptCopied ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <pre className="bg-[#1C1C19] text-[#F5F5F0] text-[11px] font-mono p-3 pt-10 rounded overflow-auto max-h-[360px] leading-snug whitespace-pre">
            {REALTIME_AGGREGATOR_SCRIPT_SNIPPET}
          </pre>
          <p className="text-[10px] text-[#6B6B61] mt-1.5 leading-relaxed">
            <b>How to use:</b> ☝️ Copy dabake aggregator sheet ke Apps Script
            editor me <b>Ctrl+A → Delete → Paste</b> karein → <b>Save</b> →
            Deploy → Manage deployments → pencil → <b>New version</b> → Deploy.
            URL same rahega — wapas paste karne ki zaroorat nahi.
          </p>
        </div>
      )}
    </div>
  );
}


/**
 * Admin bulk-paste card for Resend DAC — Bharat Gas only.
 *
 * Admin pastes rows from the daily sheet; the server:
 *   1. resolves each row's typed agency name (Bharat Gas only — non-Bharat
 *      rows get bucketed as "unmatched" with a specific reason),
 *   2. attributes each row's `created_by` to the per-row `Staff` cell so
 *      the resend entry shows up under that staff's Resend Desk,
 *   3. broadcasts via WebSocket so every logged-in staff/agency instantly
 *      sees the pending items in their view.
 *
 * The dual-tick resolution flow (staff-side + agency-side) is unchanged —
 * this card is a paste-and-go on-ramp, not a replacement for the existing
 * workflow. Ledger totals are untouched.
 */
function AdminBulkResendCard() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 mb-1">
            <Upload className="w-4 h-4 text-[#385C4B]" />
            <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase">
              Bulk Resend Paste · Bharat Gas
            </h3>
          </div>
          <p className="text-xs text-[#6B6B61] leading-relaxed">
            Excel se rows paste karo — server per-row parse karega, agency
            match karega (<b>sirf Bharat Gas / BPCL</b>), aur har row ka
            <code className="bg-[#F5F5F0] mx-1 px-1 rounded font-mono">Staff</code>{" "}
            column dekh ke us staff ke Resend Desk pe entry create karega.
            Agency side pe Chat me{" "}
            <b>&quot;Resend DAC&quot;</b> button me pending ho jayega. Dual-tick
            resolution existing rule se hoga. Ledger untouched.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="admin-bulk-resend-open"
          className="px-4 py-2 text-sm bg-[#385C4B] text-white rounded hover:bg-[#2d4a3c] font-semibold flex items-center gap-2 whitespace-nowrap"
        >
          <Upload className="w-4 h-4" /> Paste rows
        </button>
      </div>
      {open && (
        <ResendBulkPasteModal
          onClose={() => setOpen(false)}
          onCommitted={() => setOpen(false)}
          title="Admin · Bulk Resend Paste (Bharat Gas only)"
          subtitle="Attribute rows to their sub-staff in one paste"
        />
      )}
    </div>
  );
}


/**
 * Resend Sync card — single shared Google Sheet where the ops team posts
 * pending resend rows. Chat-side Resend modal reads from this sheet (gviz)
 * and stamps confirmations back via webhook.
 */
function ResendSyncCard() {
  const [sheetUrl, setSheetUrl] = useState("");
  const [savedSheetUrl, setSavedSheetUrl] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [savedWebhookUrl, setSavedWebhookUrl] = useState("");
  const [lastSync, setLastSync] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSheetUrl(data.resend_sheet_url || "");
      setSavedSheetUrl(data.resend_sheet_url || "");
      setWebhookUrl(data.resend_webhook_url || "");
      setSavedWebhookUrl(data.resend_webhook_url || "");
      setLastSync({
        at: data.last_resend_sync_at,
        ok: data.last_resend_sync_ok,
        err: data.last_resend_sync_error,
      });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = sheetUrl !== savedSheetUrl || webhookUrl !== savedWebhookUrl;

  const save = async () => {
    setBusy("save");
    setMsg(null);
    try {
      await api.put("/admin/settings", {
        resend_sheet_url: sheetUrl.trim(),
        resend_webhook_url: webhookUrl.trim(),
      });
      setSavedSheetUrl(sheetUrl.trim());
      setSavedWebhookUrl(webhookUrl.trim());
      setMsg({ type: "ok", text: "Saved · agency chat → Resend DAC button se test karein" });
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  const testWebhook = async () => {
    setBusy("test");
    setMsg(null);
    try {
      const { data } = await api.post("/admin/resend-webhook/test");
      if (data.ok) {
        setMsg({ type: "ok", text: `Webhook OK · sheet responded ${data.status}` });
      } else {
        setMsg({ type: "err", text: `Webhook failed · ${data.error || "unknown"}` });
      }
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  const copyScript = () => {
    navigator.clipboard
      .writeText(RESEND_LOG_SCRIPT_SNIPPET)
      .then(() => {
        setScriptCopied(true);
        setTimeout(() => setScriptCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm" data-testid="resend-sync-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Resend Sync · agency confirms resent done
          </h3>
          <p className="text-xs text-[#6B6B61] mt-1 leading-relaxed">
            Ek shared Google Sheet me team pending resend rows daalti hai
            (<b>Date · Agency · LPG ID · DAC · Mobile · Status · Resent Confirmed At · Confirmed By</b>).
            Chat me <b>"Resend DAC"</b> button khulta hai jisme us agency ke pending rows aate hain.
            Agency tick karke confirm karti hai → webhook se sheet me{" "}
            <b>Resent Confirmed At + By</b> stamp ho jata hai.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Resend Sheet URL (read)</label>
          <input
            value={sheetUrl}
            onChange={(e) => setSheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/.../edit"
            data-testid="resend-sheet-url"
            className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-[#385C4B]"
          />
          <p className="text-[10px] text-[#6B6B61] mt-1">Sheet "Anyone with link" (Viewer) access pe ho.</p>
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Resend Webhook URL (write)</label>
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://script.google.com/macros/s/.../exec"
            data-testid="resend-webhook-url"
            className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-[#385C4B]"
          />
          <p className="text-[10px] text-[#6B6B61] mt-1">Apps Script deploy karke /exec URL paste karein.</p>
        </div>
        {lastSync?.at && (
          <div className="text-[10px] text-[#6B6B61]">
            Last sync · {new Date(lastSync.at).toLocaleString()} · {lastSync.ok ? <span className="text-emerald-700">OK</span> : <span className="text-red-700">FAIL {lastSync.err}</span>}
          </div>
        )}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={save}
            disabled={!dirty || !!busy}
            data-testid="resend-save"
            className="px-4 py-1.5 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
          >
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "💾"} Save
          </button>
          <button
            onClick={testWebhook}
            disabled={!!busy || dirty || !savedWebhookUrl}
            data-testid="resend-test"
            className="px-4 py-1.5 bg-white border border-[#DCDCD3] hover:border-[#385C4B] text-[#385C4B] text-sm rounded disabled:opacity-50 flex items-center gap-2"
          >
            {busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🧪"} Test Webhook
          </button>
          <button
            onClick={() => setShowScript((v) => !v)}
            data-testid="resend-toggle-script"
            className="px-4 py-1.5 bg-white border border-[#DCDCD3] hover:border-[#385C4B] text-[#385C4B] text-sm rounded flex items-center gap-2"
          >
            {showScript ? "Hide Apps Script" : "Show Apps Script"}
          </button>
        </div>
      </div>

      {msg && (
        <div className={`mt-3 px-3 py-2 text-xs rounded ${
          msg.type === "ok" ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
                            : "bg-red-500/10 text-red-700 border border-red-500/30"
        }`} data-testid="resend-msg">{msg.text}</div>
      )}

      {showScript && (
        <div className="mt-4 relative" data-testid="resend-script-block">
          <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#A7A79D] bg-black/40 px-2 py-1 rounded">Code.gs</span>
            <button
              onClick={copyScript}
              data-testid="resend-copy-script"
              className="bg-white border border-[#DCDCD3] hover:border-[#385C4B] px-3 py-1 rounded text-[10px] font-mono tracking-[0.15em] uppercase text-[#385C4B]"
            >
              {scriptCopied ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <pre className="bg-[#1C1C19] text-[#F5F5F0] text-[11px] font-mono p-3 pt-10 rounded overflow-auto max-h-[360px] leading-snug whitespace-pre">
            {RESEND_LOG_SCRIPT_SNIPPET}
          </pre>
          <p className="text-[10px] text-[#6B6B61] mt-1.5 leading-relaxed">
            <b>How to use:</b> Copy → Resend Sheet ke Apps Script editor me <b>Ctrl+A → Delete → Paste</b> → Save → Deploy → Web app → <b>ANYONE</b> access → /exec URL upar paste karein → Save → Test Webhook ✓.
          </p>
        </div>
      )}
    </div>
  );
}

const RESEND_LOG_SCRIPT_SNIPPET = `/**
 * XYZ Chatting · RESEND LOG SHEET
 *
 * 3 actions handled:
 *   • action: "append"     — when staff files a NEW resend request from the
 *     portal (/api/resend/entries). Appends a fresh row at the bottom.
 *   • action: "mark_sent"  — when chat modal first delivers a sheet row to
 *     agency: stamps E="sent to agency" + F=timestamp (ONLY if E is empty).
 *     Idempotent: re-running mark_sent on already-sent row is a no-op.
 *   • action: "confirm"    — when agency ticks (resend done): stamps
 *     G=Confirmed At + H=Confirmed By (always overwrites latest tick).
 *
 * Schema (row 1 header):
 *   A:Date · B:Agency · C:LPG ID · D:Agent/Mobile · E:Status ·
 *   F:Sent At · G:Confirmed At · H:Confirmed By · I:DAC · J:Reason ·
 *   K:Created By
 */
const RESEND_COL_DATE = 1;
const RESEND_COL_AGENCY = 2;
const RESEND_COL_LPG = 3;
const RESEND_COL_AGENT = 4;
const RESEND_COL_STATUS = 5;
const RESEND_COL_SENT_AT = 6;
const RESEND_COL_CONFIRMED_AT = 7;
const RESEND_COL_CONFIRMED_BY = 8;
const RESEND_COL_DAC = 9;
const RESEND_COL_REASON = 10;
const RESEND_COL_CREATED_BY = 11;

function doPost(e) {
  const lock = LockService.getScriptLock();
  lock.waitLock(15000);
  try {
    const body = JSON.parse(e.postData.contents || "{}");
    if (body.is_test) {
      return out({ ok: true, mode: "test", received: (body.rows || []).length });
    }
    const action = String(body.action || "confirm").toLowerCase();
    const sh = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();

    // ────────── APPEND (portal-direct entry) ──────────
    if (action === "append") {
      const r = body.row || {};
      const lpg = String(r.lpg_id || "").trim();
      if (!lpg) return out({ ok: false, error: "lpg_id missing" });
      const nextRow = sh.getLastRow() + 1;
      sh.getRange(nextRow, RESEND_COL_DATE).setValue(r.date || "");
      sh.getRange(nextRow, RESEND_COL_AGENCY).setValue(r.agency || "");
      sh.getRange(nextRow, RESEND_COL_LPG).setValue(lpg);
      sh.getRange(nextRow, RESEND_COL_AGENT).setValue(r.mobile || "");
      sh.getRange(nextRow, RESEND_COL_STATUS).setValue(r.status || "sent to agency");
      sh.getRange(nextRow, RESEND_COL_SENT_AT).setValue(r.created_at || "");
      sh.getRange(nextRow, RESEND_COL_DAC).setValue(r.dac || "");
      sh.getRange(nextRow, RESEND_COL_REASON).setValue(r.reason || "");
      sh.getRange(nextRow, RESEND_COL_CREATED_BY).setValue(r.created_by || "");
      return out({ ok: true, action: "append", row: nextRow });
    }

    const rows = body.rows || [];
    if (!rows.length) return out({ ok: true, updated: 0, note: "no rows" });
    const lastRow = sh.getLastRow();
    if (lastRow < 2) return out({ ok: true, updated: 0, note: "empty sheet" });
    const lpgVals = sh.getRange(2, RESEND_COL_LPG, lastRow - 1, 1).getValues();
    const statusVals = sh.getRange(2, RESEND_COL_STATUS, lastRow - 1, 1).getValues();
    const lpgIndex = {};
    for (let i = 0; i < lpgVals.length; i++) {
      const v = String(lpgVals[i][0] || "").replace(/\\.\\d+$/, "").trim();
      if (v) lpgIndex[v] = { rowNum: i + 2, existingStatus: String(statusVals[i][0] || "").trim() };
    }
    let updated = 0;
    let skipped = 0;
    let missing = 0;
    for (const r of rows) {
      const lpg = String(r.lpg_id || "").trim();
      if (!lpg) continue;
      const hit = lpgIndex[lpg];
      if (!hit) { missing++; continue; }
      if (action === "mark_sent") {
        if (hit.existingStatus) { skipped++; continue; }
        sh.getRange(hit.rowNum, RESEND_COL_STATUS).setValue("sent to agency");
        sh.getRange(hit.rowNum, RESEND_COL_SENT_AT).setValue(r.timestamp || "");
        updated++;
      } else {
        sh.getRange(hit.rowNum, RESEND_COL_CONFIRMED_AT).setValue(r.confirmed_at || "");
        sh.getRange(hit.rowNum, RESEND_COL_CONFIRMED_BY).setValue(r.confirmed_by || "");
        updated++;
      }
    }
    return out({ ok: true, action: action, updated: updated, skipped: skipped, missing: missing });
  } catch (err) {
    return out({ ok: false, error: String(err) });
  } finally {
    lock.releaseLock();
  }
}

function doGet() {
  return out({ ok: true, service: "resend-log" });
}

/**
 * Manual trigger menu: sheet kholne par "🚀 Resend Tools" aata hai.
 * "Mark All Pending as Sent" — har row jisme E khali hai aur agency+LPG
 * dono valid hain, stamp ho jata hai (manual fallback if auto-fire missed).
 */
function onOpen() {
  SpreadsheetApp.getUi()
    .createMenu("🚀 Resend Tools")
    .addItem("Mark All Pending as Sent", "bulkMarkSent")
    .addToUi();
}

function bulkMarkSent() {
  const sh = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  const lastRow = sh.getLastRow();
  if (lastRow < 2) { SpreadsheetApp.getUi().alert("Sheet khali hai."); return; }
  const tz = SpreadsheetApp.getActiveSpreadsheet().getSpreadsheetTimeZone() || "Asia/Kolkata";
  const ts = Utilities.formatDate(new Date(), tz, "dd-MM-yyyy hh:mm a");
  const data = sh.getRange(2, 1, lastRow - 1, 8).getValues();
  let stamped = 0;
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    const agency = String(row[1] || "").trim();
    const lpg = String(row[2] || "").replace(/\\.\\d+$/, "").trim();
    const status = String(row[4] || "").trim();
    if (!agency || !lpg || status) continue;
    sh.getRange(i + 2, RESEND_COL_STATUS).setValue("sent to agency");
    sh.getRange(i + 2, RESEND_COL_SENT_AT).setValue(ts);
    stamped++;
  }
  SpreadsheetApp.getUi().alert("✓ " + stamped + " rows marked as sent.");
}

function out(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj))
    .setMimeType(ContentService.MimeType.JSON);
}`;



function PendingCleanupCard() {
  // Default cutoff = yesterday IST (so today's fresh deliveries stay
  // Pending; everything ≤ yesterday auto-marks as delivered).
  const yesterdayIst = React.useMemo(() => {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60000);
    return ist.toISOString().slice(0, 10);
  }, []);
  const [cutoff, setCutoff] = useState(yesterdayIst);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const run = async (dry_run) => {
    setBusy(dry_run ? "preview" : "clean");
    setMsg(null);
    try {
      const { data } = await api.post("/admin/delivery/mark-old-delivered", {
        cutoff_date: cutoff,
        dry_run,
      });
      setPreview(data);
      if (!dry_run) {
        setMsg({
          type: "ok",
          text: `Cleaned · ${data.entries_marked_delivered} pending entries marked delivered across ${data.groups_affected} groups.`,
        });
      }
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5" data-testid="pending-cleanup-card">
      <div className="flex items-baseline gap-2">
        <span>🧹</span>
        <h3 className="text-lg font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Clear Old Pending Deliveries
        </h3>
      </div>
      <p className="text-xs text-[#6B6B61] mt-1.5 leading-relaxed">
        URGENT banner me har group me purani pending deliveries dikh rahi hain? Ye tool
        ek click me sab OLD pending entries ko <b>DELIVERED</b> mark kar dega — sirf uss
        cutoff date tak. Aaj wali fresh deliveries pending hi rahengi (agencies unhein
        Delivery Dashboard se manually tick karengi). Wrong / cancelled / already-delivered
        entries UNTOUCHED rahengi.
      </p>
      <p className="text-[11px] text-[#8A6046] mt-2">
        <b>Recommendation:</b> Default cutoff = kal (yesterday IST). Pehle <b>Preview</b>
        dabao — total kitni entries touch hongi dekhne ke liye. Number sahi lage to
        <b> Mark All Old as Delivered</b> dabao.
      </p>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <label className="text-xs text-[#6B6B61]">Cutoff date (IST, inclusive):</label>
        <input
          type="date"
          value={cutoff}
          onChange={(e) => {
            setCutoff(e.target.value);
            setPreview(null);
            setMsg(null);
          }}
          data-testid="pending-cleanup-date"
          className="px-2 py-1 border border-[#DCDCD3] rounded text-sm"
        />
        <button
          onClick={() => run(true)}
          disabled={!!busy}
          data-testid="pending-cleanup-preview"
          className="px-3 py-1 text-xs font-mono rounded border border-[#385C4B] text-[#385C4B] hover:bg-[#385C4B]/5 disabled:opacity-40"
        >
          {busy === "preview" ? "Previewing…" : "Preview"}
        </button>
        <button
          onClick={() => {
            const n = preview?.entries_marked_delivered;
            const confirmMsg = n
              ? `Mark ${n} pending entries as DELIVERED across ${preview.groups_affected} groups?\n\nYe undone nahi ho sakta. Har entry pe "Auto · pre-${cutoff.split("-").reverse().join("/")} cutoff" stamp lagega.`
              : `Mark ALL pending entries dated ≤ ${cutoff} as DELIVERED?\n\nPehle Preview dabaao ya continue karne ke liye OK.`;
            if (!window.confirm(confirmMsg)) return;
            run(false);
          }}
          disabled={!!busy}
          data-testid="pending-cleanup-run"
          className="px-3 py-1 text-xs font-mono rounded bg-[#385C4B] text-white hover:bg-[#2D4A3C] disabled:opacity-40"
        >
          {busy === "clean" ? "Marking…" : "Mark All Old as Delivered"}
        </button>
      </div>
      {preview && (
        <div className="mt-3 p-3 rounded-md bg-[#F7F6EE] border border-[#DCDCD3] text-xs text-[#1C1C19] font-mono space-y-1">
          <div>
            <b>{preview.entries_marked_delivered}</b> pending entries {preview.dry_run ? "would be" : "were"} marked delivered
          </div>
          <div>
            Across <b>{preview.groups_affected}</b> groups · <b>{preview.messages_touched}</b> messages
          </div>
          <div className="text-[#6B6B61]">Stamp: {preview.label}</div>
        </div>
      )}
      {msg && (
        <div
          className={`mt-3 p-2 rounded text-sm ${
            msg.type === "ok"
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}


function DedupDayCard() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const run = async (dry_run) => {
    setBusy(dry_run ? "preview" : "clean");
    setMsg(null);
    try {
      const { data } = await api.post("/admin/dedup-day", { date, dry_run });
      if (dry_run) {
        setPreview(data);
      } else {
        setPreview(data);
        setMsg({
          type: "ok",
          text: `Cleaned · ${data.total_entries_removed} duplicate entries removed across ${data.groups_affected.length} groups (${data.total_messages_deleted} fully-redundant messages deleted).`,
        });
      }
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-white border border-[#DCDCD3] rounded-lg p-5" data-testid="dedup-day-card">
      <div className="flex items-baseline gap-2">
        <span className="text-amber-700">⚠️</span>
        <h3 className="text-lg font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Cleanup Duplicate LPG Entries by Day
        </h3>
      </div>
      <p className="text-xs text-[#6B6B61] mt-1.5 leading-relaxed">
        Agar aapne galti se <b>force-send</b> kar diya aur same LPG IDs do messages me aa gaye
        (e.g. NARELA 91 instead of 46), is tool se ek IST din ke saare groups me duplicates remove
        ho jate hain. Pehli baar jis message me LPG aaya tha wo retain hota hai (saare wrong /
        DAC marks safe rahenge), baad ki duplicate entries strip ho jati hain. Agar ek message
        ka SAARA content duplicate ho, wo message bhi delete ho jata hai.
      </p>
      <p className="text-[11px] text-[#8A6046] mt-2">
        <b>Recommendation:</b> Pehle <b>Preview</b> dabake counts dekhein. Number sahi lage to
        <b> Clean</b> dabaiye.
      </p>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <label className="text-xs text-[#6B6B61]">Date (IST):</label>
        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); setPreview(null); }}
          data-testid="dedup-day-date"
          className="px-2 py-1 border border-[#DCDCD3] rounded text-sm"
        />
        <button
          onClick={() => run(true)}
          disabled={!!busy}
          data-testid="dedup-day-preview"
          className="px-3 py-1.5 bg-white border border-[#DCDCD3] text-[#385C4B] text-sm rounded hover:border-[#385C4B] disabled:opacity-50 flex items-center gap-2"
        >
          {busy === "preview" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🔍"}
          Preview
        </button>
        <button
          onClick={() => run(false)}
          disabled={!!busy || !preview}
          data-testid="dedup-day-clean"
          className="px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
        >
          {busy === "clean" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🧹"}
          Clean Duplicates
        </button>
      </div>

      {preview && (
        <div className="mt-3 border border-[#DCDCD3] rounded p-3 text-xs" data-testid="dedup-day-preview-result">
          <div className="font-semibold mb-2 flex items-center gap-2">
            {preview.dry_run ? "Preview" : "Cleaned"} · {preview.date} ·
            <span className="font-mono text-amber-700">
              {preview.total_entries_removed} entries
            </span>
            {preview.total_messages_deleted > 0 && (
              <span className="font-mono text-red-700">
                + {preview.total_messages_deleted} empty messages
              </span>
            )}
          </div>
          {preview.groups_affected.length === 0 ? (
            <p className="text-[#6B6B61]">No duplicates found for this date.</p>
          ) : (
            <table className="w-full text-[11px] font-mono">
              <thead>
                <tr className="text-left text-[#6B6B61]">
                  <th className="py-1">Agency</th>
                  <th className="py-1 text-right">Entries removed</th>
                  <th className="py-1 text-right">Messages touched</th>
                  <th className="py-1 text-right">Messages deleted</th>
                </tr>
              </thead>
              <tbody>
                {preview.groups_affected.map((g) => (
                  <tr key={g.group_id} className="border-t border-[#DCDCD3]">
                    <td className="py-1">{g.name}</td>
                    <td className="py-1 text-right">{g.entries_removed}</td>
                    <td className="py-1 text-right">{g.messages_touched}</td>
                    <td className="py-1 text-right">{g.messages_deleted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {msg && (
        <div
          className={`mt-3 px-3 py-2 text-xs rounded ${
            msg.type === "ok"
              ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
              : "bg-red-500/10 text-red-700 border border-red-500/30"
          }`}
          data-testid="dedup-day-msg"
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}

function SheetsTab() {
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [showScript, setShowScript] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setUrl(data.lpg_sheet_webhook_url || "");
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    setMsg(null);
    try {
      const { data } = await api.put("/admin/settings", { lpg_sheet_webhook_url: url });
      setSettings(data);
      setMsg({ type: "ok", text: "Webhook URL saved" });
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const testWebhook = async () => {
    setBusy("test");
    setMsg(null);
    try {
      const { data } = await api.post("/admin/sheets/test");
      setMsg({ type: "ok", text: `Webhook reachable · HTTP ${data.status}. Check your sheet for a 'TEST:0' row.` });
      await load();
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const backfill = async () => {
    if (!window.confirm("Push ALL existing LPG entries to the sheet? This will REPLACE current rows (header preserved).")) return;
    setBusy("backfill");
    setMsg(null);
    try {
      const { data } = await api.post("/admin/sheets/backfill");
      const aggCount = data.count || 0;
      const batchCount = data.batch_count || 0;
      const msgParts = [`${aggCount} daily rows (Sheet1)`];
      if (batchCount > 0) msgParts.push(`${batchCount} batch rows (Sheet2)`);
      setMsg({
        type: "ok",
        text: `Synced ${msgParts.join(" · ")}. Sheet now matches DB.`,
      });
      await load();
    } catch (e) {
      setMsg({ type: "error", text: formatApiError(e) });
    } finally { setBusy(""); }
  };

  const copyScript = async () => {
    try { await navigator.clipboard.writeText(APPS_SCRIPT_SNIPPET); setMsg({ type: "ok", text: "Apps Script copied to clipboard" }); } catch { setMsg({ type: "error", text: "Copy failed" }); }
  };

  const lastSync = settings?.last_sync_at;
  const lastOk = settings?.last_sync_ok;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="sheets-tab">
      <div>
        <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61]">
          Integrations
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Google Sheets · Daily Report
        </h2>
        <p className="text-sm text-[#6B6B61] mt-1.5 leading-relaxed">
          <b>Sheet1</b> par har din ka per-agency aggregate (Date, Agency, Sent, Done, Pending, Billable).
          <b> Sheet2</b> par per-batch event log — same agency 5 baar upload = 5 alag rows with IST time.
          Koi DAC code ya raw data sheet pe nahi jaata.
        </p>
        <div className="mt-3 bg-[#E3D9C0]/40 border border-[#A38B5A]/40 rounded-md px-3 py-2 text-xs text-[#5C4A28] leading-relaxed">
          <b>Script v5 update (Iter88 · Sheet2 Refresh Fix):</b> Apps Script ko niche se copy karke <b>Extensions → Apps Script</b> mein replace karein, phir <b>Deploy → Manage deployments → ✏️ → Version = New version → Deploy</b>. Naya behaviour: <b>Sync All Existing Data</b> button ab Sheet2 ke bhi stale rows (deleted messages, purane test groups, DedupTest fixtures) auto-clean karega — Sheet1 jaisa hi refresh. Sheet2 remarks (columns L..T) preserved rahenge un rows ke liye jo abhi bhi live hain.
        </div>
      </div>

      <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase">
            Setup (one-time)
          </h3>
          <button
            onClick={() => setShowScript(!showScript)}
            data-testid="sheets-toggle-script-button"
            className="text-xs text-[#385C4B] hover:text-[#2D4A3C] underline"
          >
            {showScript ? "Hide" : "Show"} Apps Script code
          </button>
        </div>
        <ol className="text-sm text-[#1C1C19] space-y-2 list-decimal list-inside leading-relaxed">
          <li>Ek nayi Google Sheet banayein (<a className="text-[#385C4B] underline" href="https://sheets.new" target="_blank" rel="noreferrer">sheets.new</a>).</li>
          <li>Top menu: <b>Extensions → Apps Script</b>.</li>
          <li>Sample code hatake, neeche diya Apps Script paste karein (<b>Copy</b> button use karein).</li>
          <li>Save (disk icon), phir <b>Deploy → New deployment</b>.</li>
          <li>Settings (gear) icon: type = <b>Web app</b>. Execute as = <b>Me</b>. Who has access = <b>Anyone</b>. <b>Deploy</b> dabayein.</li>
          <li>Google access maange to allow karein. <b>Web app URL</b> copy karke neeche paste karein.</li>
          <li><b>Save</b> &rarr; <b>Test</b> &rarr; <b>Sync All</b> &mdash; done.</li>
        </ol>
        {showScript && (
          <div className="mt-4 relative">
            <button
              onClick={copyScript}
              data-testid="sheets-copy-script-button"
              className="absolute top-2 right-2 z-10 flex items-center gap-1.5 text-xs bg-[#385C4B] text-[#F5F5F0] hover:bg-[#2D4A3C] px-2.5 py-1.5 rounded transition-colors"
            >
              <CopyIcon className="w-3 h-3" /> Copy
            </button>
            <pre className="bg-[#1C1C19] text-[#E8E5DC] text-[11px] font-mono p-4 pt-10 rounded-md overflow-x-auto max-h-72 leading-relaxed">
              {APPS_SCRIPT_SNIPPET}
            </pre>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase mb-3 flex items-center gap-2">
          <LinkIcon className="w-3.5 h-3.5 text-[#385C4B]" /> Webhook URL
        </h3>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://script.google.com/macros/s/.../exec"
          data-testid="sheets-webhook-url-input"
          className="w-full px-3 py-2.5 bg-[#F5F5F0] border border-[#DCDCD3] rounded-md text-sm font-mono focus:outline-none focus:border-[#385C4B] focus:ring-1 focus:ring-[#385C4B]/30"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            onClick={save}
            disabled={busy === "save"}
            data-testid="sheets-save-button"
            className="flex items-center gap-2 bg-[#385C4B] text-[#F5F5F0] hover:bg-[#2D4A3C] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
          >
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Save URL
          </button>
          <button
            onClick={testWebhook}
            disabled={busy === "test" || !settings?.lpg_sheet_webhook_url}
            data-testid="sheets-test-button"
            className="flex items-center gap-2 bg-white border border-[#DCDCD3] text-[#1C1C19] hover:border-[#385C4B] hover:text-[#385C4B] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
          >
            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Test Connection
          </button>
          <button
            onClick={backfill}
            disabled={busy === "backfill" || !settings?.lpg_sheet_webhook_url}
            data-testid="sheets-backfill-button"
            className="flex items-center gap-2 bg-[#b45309] text-[#F5F5F0] hover:bg-[#92400e] disabled:opacity-50 px-4 py-2 rounded-md text-sm transition-colors"
          >
            {busy === "backfill" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Sync All Existing Data
          </button>
        </div>
        {msg && (
          <div
            data-testid="sheets-status-message"
            className={`mt-3 text-sm px-3 py-2 rounded-md flex items-start gap-2 ${
              msg.type === "ok"
                ? "bg-[#385C4B]/10 text-[#385C4B] border border-[#385C4B]/30"
                : "bg-[#8A6046]/10 text-[#8A6046] border border-[#8A6046]/30"
            }`}
          >
            {msg.type === "ok" ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <span>{msg.text}</span>
          </div>
        )}
      </div>

      <EmailNotifyCard />
      {/* Global Complaint Sheet card removed Feb 2026 — per-team-member
          routing lives on /complaints → "Google Sheet Routing" panel. */}
      <DeliveredWebhookCard />
      <RealtimeAggregatorCard />
      <AdminBulkResendCard />
      <ResendSyncCard />
      <SheetInboxCard />
      <PendingCleanupCard />
      <DedupDayCard />

      {settings && (lastSync || settings.last_sync_error) && (
        <div className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[#1C1C19] tracking-wide uppercase mb-3">
            Last Sync Status
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">When</div>
              <div className="text-[#1C1C19] mt-1">{lastSync ? new Date(lastSync).toLocaleString() : "-"}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Status</div>
              <div className={`mt-1 font-semibold ${lastOk ? "text-[#385C4B]" : "text-[#8A6046]"}`}>
                {lastOk ? `OK (HTTP ${settings.last_sync_status || "?"})` : "Failed"}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Action</div>
              <div className="text-[#1C1C19] mt-1 capitalize">{settings.last_sync_action || "-"}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Rows</div>
              <div className="text-[#1C1C19] mt-1 font-mono">{settings.last_sync_count ?? "-"}</div>
            </div>
          </div>
          {settings.last_sync_error && (
            <div className="mt-3 text-xs text-[#8A6046] bg-[#8A6046]/10 border border-[#8A6046]/30 rounded p-2 font-mono break-all">
              {settings.last_sync_error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Staff Sheets Tab — admin manages staff Google Sheet links and assigns each
// to one or more agency groups. Members of those groups see the linked sheets
// in their chat (view-only, real-time via Google).
// =============================================================================
function StaffSheetsTab() {
  const [sheets, setSheets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, name, url, group_ids}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { sorted: sortedSheets, label: sheetSortLabel, cycle: cycleSheetSort } = useNameSort(
    sheets,
    "admin_staff_sheets"
  );

  const load = async () => {
    setErr("");
    try {
      const [s, g] = await Promise.all([
        api.get("/admin/staff-sheets"),
        api.get("/groups"),
      ]);
      setSheets(s.data || []);
      setGroups(g.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  // Map group_id → group name for quick rendering in the agencies column
  const groupNameById = React.useMemo(() => {
    const m = {};
    (groups || []).forEach((g) => { m[g.id] = g.name; });
    return m;
  }, [groups]);

  const startCreate = () => setEditing({ name: "", url: "", group_ids: [] });
  const startEdit = (s) =>
    setEditing({ id: s.id, name: s.name, url: s.url, group_ids: s.group_ids || [] });

  const toggleGroup = (gid) => {
    if (!editing) return;
    const current = new Set(editing.group_ids || []);
    if (current.has(gid)) {
      current.delete(gid);
    } else {
      current.add(gid);
    }
    setEditing({ ...editing, group_ids: Array.from(current) });
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.url.trim()) {
      setErr("Name and URL dono zaroori hain.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        name: editing.name.trim(),
        url: editing.url.trim(),
        group_ids: editing.group_ids || [],
      };
      if (editing.id) {
        await api.patch(`/admin/staff-sheets/${editing.id}`, body);
      } else {
        await api.post("/admin/staff-sheets", body);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete staff sheet "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/staff-sheets/${s.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="staff-sheets-tab">
      <div>
        <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61] flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8553D] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C8553D]"></span>
          </span>
          Real-time delivery links
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Real Time Sheets · {sheets.length}
        </h2>
        <p className="text-xs text-[#6B6B61] mt-1.5 leading-relaxed">
          Har agency ka Google Sheet yahan add karein —{" "}
          <b>specific agencies select karke scope</b> kar do, ya khali rakho
          to <b>saari agencies ko visible</b> rahega (read-only, real-time).
          Agency members chat sidebar ke <b>&ldquo;REAL TIME&rdquo;</b>{" "}
          button (live red dot) se direct view kar payenge. Default mein{" "}
          <b>aaj ki date</b> ka tab khulta hai; agency chahein to old date
          tabs niche se select kar sakti hain.
        </p>
        <p className="text-[11px] text-[#8A6046] mt-2 leading-relaxed">
          <b>Tip:</b> Google Sheet ki Share settings me <b>&ldquo;Anyone with the
          link &rarr; Viewer&rdquo;</b> set karna zaroori hai, warna agency ko
          access nahi milega.
        </p>
      </div>

      {err && (
        <div className="bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] rounded px-3 py-2 text-sm">
          {err}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          onClick={startCreate}
          data-testid="staff-add-button"
          className="flex items-center gap-2 bg-[#385C4B] text-[#F5F5F0] hover:bg-[#2D4A3C] px-4 py-2 rounded-md text-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Real-Time Sheet
        </button>
        {sheets.length > 1 && (
          <button
            onClick={cycleSheetSort}
            data-testid="admin-sheets-sort-toggle"
            title="Sort order — click to cycle (A-Z / Z-A / Default)"
            className="bg-white border border-[#DCDCD3] hover:border-[#385C4B] text-[#385C4B] px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-xs font-mono tracking-[0.1em] uppercase"
          >
            <ArrowUpDown className="w-3.5 h-3.5" strokeWidth={1.75} />
            {sheetSortLabel}
          </button>
        )}
      </div>

      <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F5F5F0] text-left">
            <tr>
              {["Staff Name", "Agencies", "URL", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DCDCD3]">
            {sortedSheets.map((s) => {
              const scopedIds = s.group_ids || [];
              return (
                <tr key={s.id} data-testid="staff-row">
                  <td className="px-4 py-3 text-sm font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {s.name}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {scopedIds.length === 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#385C4B]/10 text-[#385C4B] font-mono uppercase tracking-wider text-[10px]">
                        All Agencies
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {scopedIds.map((gid) => (
                          <span
                            key={gid}
                            className="inline-flex items-center px-2 py-0.5 rounded bg-[#8A6046]/10 text-[#8A6046] font-mono text-[10px]"
                            title={groupNameById[gid] || gid}
                          >
                            {groupNameById[gid] || gid.slice(0, 8)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#6B6B61] max-w-md truncate" title={s.url}>
                    {s.url}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => startEdit(s)}
                      data-testid="staff-edit-button"
                      className="p-1.5 text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#385C4B]/5 rounded mr-1"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                    <button
                      onClick={() => remove(s)}
                      data-testid="staff-delete-button"
                      className="p-1.5 text-[#6B6B61] hover:text-[#8A6046] hover:bg-[#8A6046]/5 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {sheets.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-[#6B6B61]">
                Koi real-time sheet abhi tak add nahi kiya. &ldquo;Add Real-Time Sheet&rdquo; se shuru karein.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
          onClick={() => !busy && setEditing(null)}
        >
          <div
            className="bg-white rounded-lg p-5 w-full max-w-lg border border-[#DCDCD3] shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="staff-edit-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {editing.id ? "Edit Real-Time Sheet" : "Add Real-Time Sheet"}
              </h2>
              <button onClick={() => setEditing(null)} className="text-[#6B6B61]" disabled={busy}>
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">Staff Name</label>
            <input
              autoFocus
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              placeholder="e.g. Ramesh (Delivery Boy 1)"
              maxLength={60}
              data-testid="staff-name-input"
              className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm focus:outline-none focus:border-[#385C4B]"
            />
            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] mt-3 block">
              Google Sheet URL
            </label>
            <input
              value={editing.url}
              onChange={(e) => setEditing({ ...editing, url: e.target.value })}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              data-testid="staff-url-input"
              className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-[#385C4B]"
            />

            <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61] mt-3 block">
              Scope · Which agencies see this sheet?
            </label>
            <div
              className="mt-1 border border-[#DCDCD3] rounded p-2 max-h-48 overflow-y-auto bg-[#F5F5F0]/40"
              data-testid="staff-agencies-picker"
            >
              {groups.length === 0 ? (
                <div className="text-xs text-[#6B6B61] px-2 py-1">
                  No groups available.
                </div>
              ) : (
                <div className="space-y-1">
                  {[...groups]
                    .sort((a, b) =>
                      String(a.name || "").localeCompare(
                        String(b.name || ""),
                        "en",
                        { numeric: true, sensitivity: "base" }
                      )
                    )
                    .map((g) => {
                    const checked = (editing.group_ids || []).includes(g.id);
                    return (
                      <label
                        key={g.id}
                        className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white cursor-pointer text-sm"
                        data-testid="staff-agency-option"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleGroup(g.id)}
                          className="accent-[#385C4B] cursor-pointer"
                        />
                        <span className="text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
                          {g.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="text-[11px] text-[#6B6B61] mt-2 leading-relaxed">
              {(editing.group_ids || []).length === 0 ? (
                <>
                  <b>Koi agency select nahi ki</b> — sheet <b>saari agencies</b> ko
                  visible rahega (legacy default).
                </>
              ) : (
                <>
                  Sheet sirf <b>{(editing.group_ids || []).length}</b>{" "}
                  selected agency/agencies ko dikhega. Doosri agencies ko nahi
                  milega.
                </>
              )}{" "}
              Default mein agency members <b>aaj ki date</b> ka tab dekhenge, niche
              tab strip se old dates pe ja sakte hain.
            </p>
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setEditing(null)}
                disabled={busy}
                className="px-3 py-1.5 text-sm text-[#6B6B61] hover:bg-[#F5F5F0] rounded"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={busy}
                data-testid="staff-save-button"
                className="px-4 py-1.5 bg-[#385C4B] text-white text-sm rounded hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center gap-2"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {editing.id ? "Save Changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Groups Tab — admin can rename any group (1-click). Used to fix mis-typed
// agency names so Bulk Paste / Reports always match the official register.
// =============================================================================
function GroupsTab() {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null); // { id, name }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const { sorted: sortedGroups, label: sortLabel, cycle: cycleSort } = useNameSort(
    groups,
    "admin_groups"
  );
  // Case-insensitive substring filter — matches the "updated name" pattern
  // (e.g. admin types "gautam" and sees GAUTAM ENTERRISES BHARAT GAS row).
  const filteredGroups = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedGroups;
    return sortedGroups.filter((g) => (g.name || "").toLowerCase().includes(q));
  }, [sortedGroups, search]);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/groups");
      setGroups(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const saveCoolingLink = async (g, target) => {
    try {
      await api.put(`/groups/${g.id}/cooling-link`, { cooling_agency_id: target || null });
      setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...x, cooling_agency_id: target || null } : x)));
      toast.success(`Cooling link saved · ${g.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (g) => {
    setEditing({ id: g.id, name: g.name });
    setErr("");
  };

  const saveEdit = async () => {
    if (!editing || !editing.name.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await api.patch(`/groups/${editing.id}`, { name: editing.name.trim() });
      setEditing(null);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const cancelEdit = () => setEditing(null);

  const [syncBusy, setSyncBusy] = useState(false);
  const resyncFromOwners = async () => {
    if (!window.confirm(
      "Sync every group's name to its owner's display_name?\n\nThis renames groups that have exactly ONE non-admin member so the group name matches that user's current display_name (e.g. GAUTAMENTERRISES → GAUTAM ENTERRISES BHARAT GAS). It also refreshes historical resend entries.\n\nSafe to run repeatedly. Conflicts are skipped, not overwritten."
    )) return;
    setSyncBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/groups/resync-from-owners");
      const renamed = data.renamed || [];
      if (renamed.length === 0) {
        setErr("All groups already in sync. Nothing changed.");
      } else {
        setErr(
          `Renamed ${renamed.length} group(s):\n` +
            renamed
              .slice(0, 8)
              .map((r) => `  • ${r.from} → ${r.to}`)
              .join("\n") +
            (renamed.length > 8 ? `\n  … +${renamed.length - 8} more` : "")
        );
      }
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSyncBusy(false);
    }
  };

  const handleDelete = async (g) => {
    const first = window.prompt(
      `Delete group "${g.name}"?\n\nThis will:\n• Permanently remove the group from all users\n• Keep all messages & LPG entries in DB (audit-safe)\n\nType the group name EXACTLY to confirm:`
    );
    if (first === null) return;
    if (first.trim() !== (g.name || "").trim()) {
      window.alert(
        `Name didn't match — got "${first.trim()}" expected "${g.name}". Delete cancelled.`
      );
      return;
    }
    setDeleting(g.id);
    setErr("");
    try {
      await api.delete(`/groups/${g.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="groups-tab">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#6B6B61]">
            Agency directory
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Groups · {groups.length}
          </h2>
          <p className="text-xs text-[#6B6B61] mt-1.5">
            Click the pencil icon to rename any group. Renaming preserves all
            messages, LPG history, sheet sync mapping, and aliases.
          </p>
        </div>
        <button
          onClick={cycleSort}
          data-testid="admin-groups-sort-toggle"
          title="Sort order — click to cycle (A-Z / Z-A / Default)"
          className="bg-white border border-[#DCDCD3] hover:border-[#385C4B] text-[#385C4B] px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-xs font-mono tracking-[0.1em] uppercase"
        >
          <ArrowUpDown className="w-3.5 h-3.5" strokeWidth={1.75} />
          {sortLabel}
        </button>
        <button
          onClick={resyncFromOwners}
          disabled={syncBusy}
          data-testid="admin-groups-resync-owners"
          title="One-click safety net — rename every solo-owner group to match its owner's current display_name (fixes stale post-rename drift)."
          className="bg-[#385C4B] hover:bg-[#385C4B]/90 disabled:opacity-50 text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-xs font-mono tracking-[0.1em] uppercase"
        >
          {syncBusy ? "Syncing…" : "Sync from owners"}
        </button>
      </div>
      {err && (
        <div className="bg-[#8A6046]/10 border border-[#8A6046]/30 text-[#8A6046] rounded px-3 py-2 text-sm whitespace-pre-wrap">
          {err}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search groups by name…"
          data-testid="admin-groups-search"
          className="flex-1 max-w-md bg-white border border-[#DCDCD3] focus:border-[#385C4B] focus:outline-none focus:ring-2 focus:ring-[#385C4B]/20 rounded-md px-3 py-2 text-sm"
        />
        <span className="text-[10px] font-mono text-[#6B6B61] tracking-wider">
          {filteredGroups.length}/{groups.length}
        </span>
        <button
          onClick={() => setShowDelete((v) => !v)}
          data-testid="groups-toggle-delete"
          className={`ml-auto px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded border ${
            showDelete
              ? "bg-rose-50 border-rose-300 text-rose-700"
              : "bg-white border-[#DCDCD3] text-[#6B6B61] hover:border-[#385C4B] hover:text-[#385C4B]"
          }`}
          title="Toggle delete mode (hidden by default to avoid accidents)"
        >
          {showDelete ? "Hide delete" : "Show delete"}
        </button>
      </div>
      <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F5F5F0] text-left">
            <tr>
              {["Name", "Members", "Cooling Link", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono tracking-[0.2em] uppercase text-[#6B6B61]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DCDCD3]">
            {filteredGroups.map((g) => (
              <tr key={g.id} data-testid="groups-row">
                <td className="px-4 py-3 text-sm text-[#1C1C19]">
                  {editing && editing.id === g.id ? (
                    <input
                      autoFocus
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEdit();
                        if (e.key === "Escape") cancelEdit();
                      }}
                      data-testid="groups-rename-input"
                      maxLength={80}
                      className="w-full px-2 py-1 border border-[#385C4B] rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30"
                    />
                  ) : (
                    <span className="font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {g.name}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-[#6B6B61]">
                  {g.member_count ?? (g.member_ids?.length ?? "—")}
                </td>
                <td className="px-4 py-3">
                  <select
                    value={g.cooling_agency_id || ""}
                    onChange={(e) => saveCoolingLink(g, e.target.value)}
                    data-testid={`groups-cooling-link-${g.id}`}
                    className="max-w-[220px] px-2 py-1 text-xs border border-[#DCDCD3] rounded bg-white"
                  >
                    <option value="">Khud ka data (default)</option>
                    {sortedGroups.filter((x) => x.id !== g.id).map((x) => (
                      <option key={x.id} value={x.id}>{x.name}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-[#6B6B61]">
                  {g.created_at ? new Date(g.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  {editing && editing.id === g.id ? (
                    <div className="flex gap-1 justify-end">
                      <button
                        onClick={saveEdit}
                        disabled={busy}
                        data-testid="groups-rename-save"
                        className="px-2.5 py-1 text-xs bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-2.5 py-1 text-xs bg-white border border-[#DCDCD3] text-[#6B6B61] rounded hover:bg-[#F5F5F0]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => startEdit(g)}
                        data-testid="groups-rename-button"
                        title="Rename"
                        className="p-1.5 text-[#6B6B61] hover:text-[#385C4B] hover:bg-[#385C4B]/5 rounded"
                      >
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </button>
                      {showDelete && (
                        <button
                          onClick={() => handleDelete(g)}
                          disabled={deleting === g.id}
                          data-testid="groups-delete-button"
                          title="Delete group (permanent)"
                          className="p-1.5 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-50"
                        >
                          {deleting === g.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {groups.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-sm text-[#6B6B61]">
                  No groups yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

